import React, { useEffect, useState, useRef, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronLeft, ChevronRight, Phone, ShoppingCart, Check, ArrowRight, Info, Calculator, CheckCircle2, RefreshCw, X, Minus, Plus, ZoomIn, FileText, Download, Mail, Truck, Star } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useCart } from './CartContext';
import { categories } from '../data/products';
import { QuoteModal } from './QuoteModal';
import { useLocale, getProductDescription } from '../utils/i18n';
import { SameDayBadge } from './SameDayBadge';
import { generateSpecSheet } from '../utils/generateSpecSheet';
import { buildDeliveryLineItems } from '../utils/loadSplit';
import { SmallLoadBreakdown } from './SmallLoadBreakdown';
const TOP_SELLER_SLUGS = new Set(['5-8-minus', '1-25-minus', '5-8-clear', '1-5-drain-rock', '2-4-quarry-rock', '3-way-topsoil', 'medium-bark']);
function getDensity(productName: string): number {
  const name = productName.toLowerCase();
  if (name.includes('bark') || name.includes('play chips')) return 0.45;
  if (name.includes('compost')) return 0.6;
  if (name.includes('sand')) return 1.4;
  return 1.3;
}
function isLightMaterial(name: string): boolean {
  const n = name.toLowerCase();
  return n.includes('bark') || n.includes('compost') || n.includes('topsoil') || n.includes('50/50') || n.includes('fill dirt') || n.includes('play chips');
}
const ProductImageCarousel = ({
  images,
  alt
}: {
  images: string[];
  alt: string;
}) => {
  const [idx, setIdx] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);
  useEffect(() => {
    setIdx(0);
    setIsPaused(false);
  }, [images]);
  // Auto-advance every 5 seconds
  useEffect(() => {
    if (!images || images.length <= 1 || isPaused || isZoomed) return;
    const timer = setInterval(() => {
      setIdx(prev => prev === images.length - 1 ? 0 : prev + 1);
    }, 5000);
    return () => clearInterval(timer);
  }, [images, isPaused, isZoomed]);
  const activeImage = images && images.length > 0 ? images[idx] : '';
  return <>
      <div className="absolute inset-0 w-full h-full cursor-pointer group overflow-hidden" onClick={() => setIsZoomed(true)} onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)} data-id="element-1140">
        {!images || images.length <= 1 ? <img src={images[0]} alt={alt} className="absolute inset-0 w-full h-full object-cover" data-id="element-1141" /> : images.map((src, i) => <img key={src} src={src} alt={alt} className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500" style={{
        opacity: i === idx ? 1 : 0
      }} data-id="element-1142" />)}
      </div>

      {/* Fullscreen Zoom Overlay */}
      <AnimatePresence data-id="element-1143">
        {isZoomed && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center touch-none" onClick={() => setIsZoomed(false)} data-id="element-1144">
            <button className="absolute top-6 right-6 text-white/70 hover:text-white bg-black/50 p-2 rounded-full transition-colors z-[110]" onClick={e => {
          e.stopPropagation();
          setIsZoomed(false);
        }} aria-label="Close zoom" data-id="element-1145">
              <X className="w-6 h-6" data-id="element-1146" />
            </button>

            <motion.img initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.9,
          opacity: 0
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} src={activeImage} alt={alt} className="max-w-full max-h-full object-contain" style={{
          touchAction: 'pan-x pan-y pinch-zoom'
        }} data-id="element-1147" />
          </motion.div>}
      </AnimatePresence>
    </>;
};
function CustomSelect({
  value,
  onChange,
  options,
  placeholder,
  className = ''
}: {
  value: string;
  onChange: (val: string) => void;
  options: {
    value: string;
    label: string;
    disabled?: boolean;
  }[];
  placeholder: string;
  className?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const selectedOption = options.find(opt => opt.value === value);
  return <div className={`relative ${className}`} ref={dropdownRef} data-id="element-1148">
      <button type="button" onClick={() => setIsOpen(!isOpen)} className={`w-full px-4 py-3 bg-brand-lightgray/30 dark:bg-[#383838] border border-stone-200 dark:border-[#4b4b4b] rounded-none text-brand-charcoal dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all flex items-center justify-between font-medium ${value ? '' : 'text-stone-400'} bg-[#353535] bg-[#303030]`} data-id="element-1149">
        <span className="truncate" data-id="element-1150">
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 ml-2 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-id="element-1151" />
      </button>

      <AnimatePresence data-id="element-1152">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} transition={{
        duration: 0.15
      }} className="absolute z-50 w-full bottom-full mb-2 bg-white dark:bg-[#383838] border border-stone-200 dark:border-[#4b4b4b] rounded-lg shadow-xl overflow-hidden max-h-60 overflow-y-auto" data-id="element-1153">
            {options.map(opt => <button key={opt.value} type="button" disabled={opt.disabled} onClick={() => {
          if (!opt.disabled) {
            onChange(opt.value);
            setIsOpen(false);
          }
        }} className={`w-full text-left px-4 py-3 text-sm transition-colors ${opt.disabled ? 'text-stone-300 dark:text-white/30 cursor-not-allowed bg-stone-50 dark:bg-stone-900/50' : value === opt.value ? 'bg-brand-orange/20 text-brand-orange' : 'text-brand-charcoal dark:text-white hover:bg-stone-50 dark:hover:bg-white/10'}`} data-id="element-1154">
                {opt.label}
              </button>)}
          </motion.div>}
      </AnimatePresence>
    </div>;
}
const projectTypes = [{
  id: 'driveway',
  name: 'Driveway',
  defaultDepth: 4
}, {
  id: 'patio',
  name: 'Patio Base',
  defaultDepth: 4
}, {
  id: 'walkway',
  name: 'Walkway',
  defaultDepth: 3
}, {
  id: 'parking',
  name: 'Parking Pad',
  defaultDepth: 6
}, {
  id: 'french-drain',
  name: 'French Drain',
  defaultDepth: 12
}, {
  id: 'fill',
  name: 'General Fill',
  defaultDepth: 6
}];
const calcMaterials = [{
  id: '125-minus',
  name: '1.25" Minus',
  density: 1.4,
  priceRange: '$61'
}, {
  id: 'crushed-rock',
  name: '5/8" Minus',
  density: 1.4,
  priceRange: '$61'
}, {
  id: 'walkway-gravel',
  name: 'Walkway Gravel (3/8" Minus)',
  density: 1.4,
  priceRange: '$55'
}, {
  id: 'recycled-asphalt',
  name: 'Recycled Asphalt',
  density: 1.4,
  priceRange: '$42'
}, {
  id: 'recycled-concrete',
  name: 'Recycled Concrete',
  density: 1.4,
  priceRange: '$42'
}, {
  id: 'builders-sand',
  name: 'Builders Sand',
  density: 1.5,
  priceRange: '$64'
}, {
  id: 'type-17',
  name: 'Gravel Borrow',
  density: 1.4,
  priceRange: '$42'
}, {
  id: '58-clear',
  name: '5/8" Clean',
  density: 1.35,
  priceRange: '$77'
}, {
  id: '78-drain',
  name: '7/8" Drain Rock',
  density: 1.35,
  priceRange: '$69'
}, {
  id: '125-clear',
  name: '1.25" Clean',
  density: 1.35,
  priceRange: '$69'
}, {
  id: '15-drain',
  name: '1.5" Drain Rock',
  density: 1.35,
  priceRange: '$76'
}, {
  id: 'pea-gravel',
  name: 'Pea Gravel',
  density: 1.45,
  priceRange: '$60'
}, {
  id: 'topsoil',
  name: '3-Way Topsoil',
  density: 1.1,
  priceRange: '$48'
}, {
  id: '5050-mix',
  name: '50/50 Mix',
  density: 1.1,
  priceRange: '$57'
}, {
  id: 'fill-dirt',
  name: 'Fill Dirt',
  density: 1.3,
  priceRange: '$22'
}, {
  id: 'compost',
  name: 'Compost',
  density: 0.6,
  priceRange: '$66'
}, {
  id: 'fine-bark',
  name: 'Fine Bark',
  density: 0.45,
  priceRange: '$53'
}, {
  id: 'medium-bark',
  name: 'Medium Bark',
  density: 0.45,
  priceRange: '$53'
}, {
  id: 'coarse-bark',
  name: 'Coarse Bark',
  density: 0.45,
  priceRange: '$53'
}, {
  id: 'play-chips',
  name: 'Play Chips',
  density: 0.45,
  priceRange: '$55'
}, {
  id: 'streambed-cobble',
  name: 'Streambed Cobble',
  density: 1.5,
  priceRange: '$61'
}, {
  id: 'river-rock',
  name: 'River Rock',
  density: 1.5,
  priceRange: '$84'
}, {
  id: 'lava-rock',
  name: 'Lava Rock',
  density: 0.75,
  priceRange: '$93'
}, {
  id: 'quarry-rock',
  name: '2-4" Spalls',
  density: 1.5,
  priceRange: '$77'
}, {
  id: '48-quarry',
  name: '4-8" Spalls',
  density: 1.5,
  priceRange: '$77'
}, {
  id: '2-ballast',
  name: '2" Ballast',
  density: 1.5,
  priceRange: '$61'
}, {
  id: 'large-rocks',
  name: 'Large Rocks (1-4 Man)',
  density: 1.6,
  priceRange: '$138'
}];
interface QuizOption {
  value: string;
  label: string;
  result?: {
    name: string;
    desc: string;
  };
}
interface QuizQuestion {
  id: string;
  question: string;
  options: QuizOption[];
  dependsOn?: {
    questionId: string;
    value: string;
  };
}
const quizQuestions: QuizQuestion[] = [{
  id: 'project',
  question: 'What type of project are you working on?',
  options: [{
    value: 'driveway',
    label: 'Driveway or Parking Area'
  }, {
    value: 'drainage',
    label: 'Drainage (French drain, retaining wall)'
  }, {
    value: 'landscape',
    label: 'Landscaping or Garden Beds'
  }, {
    value: 'walkway',
    label: 'Walkway or Patio Base'
  }, {
    value: 'erosion',
    label: 'Erosion Control or Slope Stabilization'
  }, {
    value: 'concrete',
    label: 'Concrete, Masonry, or Leveling'
  }, {
    value: 'fill',
    label: 'Grading, Fill, or Raising Low Spots'
  }]
}, {
  id: 'driveway_layer',
  dependsOn: {
    questionId: 'project',
    value: 'driveway'
  },
  question: 'Which layer are you building?',
  options: [{
    label: 'The deep foundation (Base layer)',
    value: 'base',
    result: {
      name: '1.25" Minus',
      desc: 'A coarser crushed rock that compacts into a rock-solid foundation. Often used as the first layer before topping with finer gravel.'
    }
  }, {
    label: 'The top driving surface (Top layer)',
    value: 'top',
    result: {
      name: '5/8" Minus',
      desc: 'Our most popular driveway gravel — packs down tight and creates a smooth, firm driving surface.'
    }
  }, {
    label: 'Budget-friendly option (Recycled material)',
    value: 'budget',
    result: {
      name: 'Recycled Concrete or Recycled Asphalt',
      desc: 'Sustainable, affordable alternatives that perform like virgin rock. Recycled Asphalt even re-binds over time for a semi-hard surface. Great for rural driveways and parking areas.'
    }
  }]
}, {
  id: 'drainage_type',
  dependsOn: {
    questionId: 'project',
    value: 'drainage'
  },
  question: 'What kind of drainage?',
  options: [{
    label: 'Standard French Drain',
    value: 'french',
    result: {
      name: '7/8" Drain Rock',
      desc: 'The Puget Sound standard for residential French drains. Washed clean so water flows freely through it.'
    }
  }, {
    label: 'Heavy-duty / Behind Retaining Wall',
    value: 'heavy',
    result: {
      name: '1.5" Drain Rock or 1.25" Clean',
      desc: 'Larger stone with bigger voids for maximum water flow in heavy-duty drainage and retaining wall backfill.'
    }
  }]
}, {
  id: 'landscape_type',
  dependsOn: {
    questionId: 'project',
    value: 'landscape'
  },
  question: 'What are you trying to achieve?',
  options: [{
    label: 'Planting grass or new plants',
    value: 'plants',
    result: {
      name: '3-Way Topsoil',
      desc: 'A premium blend of topsoil, compost, and sand — everything your plants need to thrive from day one.'
    }
  }, {
    label: 'Amending heavy clay soil',
    value: 'amend',
    result: {
      name: '50/50 Mix',
      desc: 'Half topsoil, half compost. Packed with organic matter to break up clay and boost soil health.'
    }
  }, {
    label: 'Pure compost for enriching soil',
    value: 'compost',
    result: {
      name: 'Compost',
      desc: 'Rich, fully aged organic compost packed with beneficial microbes and nutrients. Top-dress lawns, enrich raised beds, or boost vegetable garden health.'
    }
  }, {
    label: 'Weed control and moisture retention',
    value: 'weeds',
    result: {
      name: 'Fine, Medium, or Coarse Bark',
      desc: 'Natural PNW bark mulch to protect beds, suppress weeds, and retain moisture. Fine Bark for a polished look, Medium for all-around use, Coarse for maximum longevity.'
    }
  }, {
    label: 'Decorative ground cover or dry creek bed',
    value: 'decorative',
    result: {
      name: 'Streambed Cobble, River Rock, or Lava Rock',
      desc: 'Streambed Cobble for a natural creek bed look, River Rock for smooth organic accents, or Lava Rock for bold reddish-brown color and lightweight coverage. All zero-maintenance.'
    }
  }, {
    label: 'Decorative walking path',
    value: 'path',
    result: {
      name: 'Pea Gravel',
      desc: 'Small, smooth rounded stones for a beautiful, crunching pathway that drains well.'
    }
  }, {
    label: 'Playground or play area surface',
    value: 'playground',
    result: {
      name: 'Play Chips',
      desc: 'Natural cedar wood chips screened for playground use. Soft, cushioning, and naturally insect-resistant. Meets safety standards for fall protection when installed at proper depth.'
    }
  }]
}, {
  id: 'walkway_type',
  dependsOn: {
    questionId: 'project',
    value: 'walkway'
  },
  question: 'What is the surface going to be?',
  options: [{
    label: 'Pavers or Flagstone (Need a base)',
    value: 'pavers',
    result: {
      name: '5/8" Minus + Builders Sand',
      desc: 'Use 5/8" Minus as a compacted sub-base, then a layer of Builders Sand to level and set your pavers.'
    }
  }, {
    label: 'Just gravel (Walking on it directly)',
    value: 'gravel',
    result: {
      name: 'Pea Gravel or 5/8" Minus',
      desc: 'Pea gravel for a loose decorative feel, or 5/8" Minus for a firm, packed path.'
    }
  }]
}, {
  id: 'erosion_type',
  dependsOn: {
    questionId: 'project',
    value: 'erosion'
  },
  question: 'What does the area look like?',
  options: [{
    label: 'Steep slope or embankment',
    value: 'steep',
    result: {
      name: '2-4" Quarry Spalls or Large Rocks',
      desc: 'Quarry Spalls (2-4") lock in place on slopes for standard rip-rap. For extreme erosion or shoreline protection, Large Rocks (1-4 man) provide maximum mass and stability — nothing moves them.'
    }
  }, {
    label: 'Gentle slope or ditch lining',
    value: 'gentle',
    result: {
      name: '1.25" Clean, Streambed Cobble, or 2-4" Quarry Spalls',
      desc: 'For gentle slopes, 1.25" Clean provides drainage and stability. Streambed Cobble offers a natural, decorative look while controlling erosion. For steeper ditches, step up to 2-4" Quarry Spalls.'
    }
  }, {
    label: 'Severe erosion or shoreline armoring',
    value: 'severe',
    result: {
      name: 'Large Rocks (1-4 Man)',
      desc: 'Massive quarry rocks ranging from 100 lbs to 2,000+ lbs. The ultimate solution for shoreline protection, steep bank armoring, and heavy rip-rap where nothing else will hold.'
    }
  }]
}, {
  id: 'concrete_type',
  dependsOn: {
    questionId: 'project',
    value: 'concrete'
  },
  question: 'What are you doing?',
  options: [{
    label: 'Mixing concrete or mortar',
    value: 'mixing',
    result: {
      name: 'Builders Sand',
      desc: 'Coarse, washed sand with consistent grain size — essential for mixing concrete, mortar, and stucco.'
    }
  }, {
    label: 'Leveling under pavers, a pool, or a shed',
    value: 'leveling',
    result: {
      name: 'Builders Sand',
      desc: 'Clean, coarse sand that spreads and levels easily. Perfect as a setting bed under pavers, pools, and structures.'
    }
  }]
}, {
  id: 'fill_type',
  dependsOn: {
    questionId: 'project',
    value: 'fill'
  },
  question: 'What do you need to accomplish?',
  options: [{
    label: "Raise grade or fill low spots (won't be planting)",
    value: 'grade',
    result: {
      name: 'Fill Dirt',
      desc: 'Our most affordable bulk material. Clean subsoil for raising grades, backfilling, and establishing drainage slopes.'
    }
  }, {
    label: 'Fill and then plant on top',
    value: 'plant',
    result: {
      name: 'Fill Dirt + 3-Way Topsoil',
      desc: 'Use Fill Dirt to build up the area, then top with 4-6 inches of 3-Way Topsoil for a plantable surface.'
    }
  }, {
    label: 'Affordable bulk fill for subgrade or earthwork',
    value: 'bulk',
    result: {
      name: 'Gravel Borrow',
      desc: 'A naturally occurring pit-run gravel blend that compacts well. Meets WSDOT Type 17 specs — great for building up subgrade, backfill, and general earthwork at a lower cost than crushed rock.'
    }
  }]
}];
export function Services() {
  const [activeTab, setActiveTab] = useState<'materials' | 'calculator' | 'quiz'>('materials');
  const {
    addToCart,
    openCart
  } = useCart();
  const {
    locale
  } = useLocale();
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  const [showCalcModal, setShowCalcModal] = useState(false);
  const [showQuizModal, setShowQuizModal] = useState(false);
  const [productTabs, setProductTabs] = useState<Record<string, 'overview' | 'specs' | 'docs'>>({});
  const getProductTab = (slug: string) => productTabs[slug] || 'overview';
  const setProductTab = (slug: string, tab: 'overview' | 'specs' | 'docs') => setProductTabs(prev => ({
    ...prev,
    [slug]: tab
  }));
  // Add to Cart State
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [addingToCart, setAddingToCart] = useState<string | null>(null);
  const [selectedQuantity, setSelectedQuantity] = useState<number>(12);
  const [showQuantityDropdown, setShowQuantityDropdown] = useState(false);
  const [addedFlash, setAddedFlash] = useState<string | null>(null);
  const [showAllCatalog, setShowAllCatalog] = useState(false);
  const [categoryGridView, setCategoryGridView] = useState<string | null>(null);
  const [categoryOverview, setCategoryOverview] = useState<string | null>(null);
  const [showSwipeHint, setShowSwipeHint] = useState(false);
  const [swipeHintDismissed, setSwipeHintDismissed] = useState(false);
  // Inline Calculator State (full drawer-style calc inside each product card)
  const [showInlineCalc, setShowInlineCalc] = useState(false);
  const [calcShape, setCalcShape] = useState<'rectangle' | 'circle' | 'triangle' | 'irregular'>('rectangle');
  const [calcUnit, setCalcUnit] = useState<'ft' | 'm'>('ft');
  const [calcDepthUnit, setCalcDepthUnit] = useState<'in' | 'cm'>('in');
  const [inlineCalcLength, setInlineCalcLength] = useState('');
  const [inlineCalcWidth, setInlineCalcWidth] = useState('');
  const [inlineCalcDiameter, setInlineCalcDiameter] = useState('');
  const [inlineCalcBase, setInlineCalcBase] = useState('');
  const [inlineCalcHeight, setInlineCalcHeight] = useState('');
  const [inlineCalcDepth, setInlineCalcDepth] = useState('3');
  // Swipe hint: show after 60s of browsing products in detail view
  useEffect(() => {
    if (expandedCategory && !categoryOverview && !swipeHintDismissed) {
      const timer = setTimeout(() => {
        setShowSwipeHint(true);
        // Auto-dismiss after 5 seconds
        setTimeout(() => setShowSwipeHint(false), 5000);
      }, 60000);
      return () => clearTimeout(timer);
    } else {
      setShowSwipeHint(false);
    }
  }, [expandedCategory, categoryOverview, swipeHintDismissed]);
  const handleAddToCartClick = (productName: string, categoryName: string) => {
    setAddingToCart(productName);
    setSelectedQuantity(12);
  };
  const handleConfirmAdd = (categoryName: string, productName: string, price: number) => {
    const isLight = isLightMaterial(productName);
    const maxQuantity = isLight ? 60 : 48;
    if (selectedQuantity > maxQuantity) {
      setIsQuoteModalOpen(true);
      return;
    }
    // Shared splitter enforces the 5yd-per-load minimum on a partial last
    // load (e.g. 16yd non-light → 12 + 5, never 12 + 4). Same rule the
    // estimator and cart already use.
    const lineItems = buildDeliveryLineItems(selectedQuantity, productName);
    lineItems.forEach(li => {
      addToCart({
        category: categoryName,
        product: li.product,
        quantity: li.quantity,
        pricePerYard: price,
        requestedQuantity: li.requestedQuantity
      });
    });
    setAddingToCart(null);
    setAddedFlash(productName);
  };
  // Calculator State
  const [projectType, setProjectType] = useState(projectTypes[0].id);
  const [length, setLength] = useState('');
  const [width, setWidth] = useState('');
  const [depth, setDepth] = useState(projectTypes[0].defaultDepth.toString());
  const [material, setMaterial] = useState(calcMaterials[0].id);
  const [calcResults, setCalcResults] = useState<{
    yards: number;
    tons: number;
  } | null>(null);
  useEffect(() => {
    const selectedProject = projectTypes.find(p => p.id === projectType);
    if (selectedProject) {
      setDepth(selectedProject.defaultDepth.toString());
    }
  }, [projectType]);
  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    const l = parseFloat(length);
    const w = parseFloat(width);
    const d = parseFloat(depth);
    if (isNaN(l) || isNaN(w) || isNaN(d)) return;
    const depthInFeet = d / 12;
    const cubicFeet = l * w * depthInFeet;
    const cubicYards = cubicFeet / 27;
    const selectedMaterial = calcMaterials.find(m => m.id === material);
    const density = selectedMaterial ? selectedMaterial.density : 1.4;
    const tons = cubicYards * density;
    setCalcResults({
      yards: Math.ceil(cubicYards * 10) / 10,
      tons: Math.ceil(tons * 10) / 10
    });
  };
  const handleCalcAddToCart = () => {
    if (!calcResults) return;
    const selectedMaterial = calcMaterials.find(m => m.id === material);
    if (!selectedMaterial) return;
    // Find matching product in categories
    let matchedProduct: any = null;
    let matchedCategory: string = '';
    for (const cat of categories) {
      const prod = cat.products.find(p => p.name === selectedMaterial.name);
      if (prod) {
        matchedProduct = prod;
        matchedCategory = cat.name;
        break;
      }
    }
    if (matchedProduct) {
      const roundedYards = Math.ceil(calcResults.yards);
      // Split the calculated yardage into delivery line items so the cart
      // shows a clear breakdown and the 5yd-per-load minimum is enforced
      // on the partial last load (e.g. 16yd → 12 + 5).
      const lineItems = buildDeliveryLineItems(roundedYards, matchedProduct.name);
      lineItems.forEach(li => {
        addToCart({
          category: matchedCategory,
          product: li.product,
          quantity: li.quantity,
          pricePerYard: matchedProduct.pricePerYard,
          requestedQuantity: li.requestedQuantity
        });
      });
      setAddedFlash(matchedProduct.name);
      setTimeout(() => setAddedFlash(null), 2000);
      openCart();
    } else {
      setIsQuoteModalOpen(true);
    }
  };
  // Quiz State
  const [quizAnswers, setQuizAnswers] = useState<Record<string, string>>({});
  const [quizResult, setQuizResult] = useState<{
    name: string;
    desc: string;
  } | null>(null);
  const currentQuizQuestion = quizQuestions.find(q => {
    if (!q.dependsOn) return !quizAnswers[q.id];
    return quizAnswers[q.dependsOn.questionId] === q.dependsOn.value && !quizAnswers[q.id];
  });
  const handleQuizAnswer = (questionId: string, value: string, result?: {
    name: string;
    desc: string;
  }) => {
    setQuizAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
    if (result) {
      setQuizResult(result);
    }
  };
  const resetQuiz = () => {
    setQuizAnswers({});
    setQuizResult(null);
  };
  const tabs = [{
    id: 'materials' as const,
    label: 'Our Materials'
  }, {
    id: 'calculator' as const,
    label: 'Calculator'
  }, {
    id: 'quiz' as const,
    label: 'Help Me Choose'
  }];
  // Carousel state
  const [carouselIndex, setCarouselIndex] = useState(0);
  // Reset quantity when carousel index or expanded category changes
  useEffect(() => {
    if (expandedCategory) {
      const category = categories.find(c => c.name === expandedCategory);
      if (category) {
        const product = category.products[carouselIndex];
        if (product) {
          setSelectedQuantity(12);
          setAddingToCart(null);
          setShowInlineCalc(false);
          setCalcShape('rectangle');
          setInlineCalcLength('');
          setInlineCalcWidth('');
          setInlineCalcDiameter('');
          setInlineCalcBase('');
          setInlineCalcHeight('');
        }
      }
    }
  }, [carouselIndex, expandedCategory]);
  // Auto-scroll the chip carousel so the active product chip is always visible
  useEffect(() => {
    if (!expandedCategory) return;
    if (categoryGridView === expandedCategory) return;
    if (categoryOverview === expandedCategory) return;
    // Wait a frame for the DOM to render the chips
    const timer = setTimeout(() => {
      const activeChip = document.querySelector<HTMLElement>(`[data-carousel-category="${expandedCategory}"] [data-chip-index="${carouselIndex}"]`);
      if (activeChip) {
        activeChip.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      }
    }, 50);
    return () => clearTimeout(timer);
  }, [carouselIndex, expandedCategory, categoryGridView, categoryOverview]);
  // Handle navigation from Navbar shop dropdown
  useEffect(() => {
    const handleMaterialsNav = () => {
      const params = new URLSearchParams(window.location.search);
      const cat = params.get('cat');
      const prod = params.get('prod');
      if (cat) {
        setExpandedCategory(cat);
        setCarouselIndex(prod ? parseInt(prod, 10) : 0);
        setActiveTab('materials');
        // Clear query params so a refresh doesn't re-open the card
        const cleanUrl = window.location.pathname + window.location.hash.split('?')[0];
        window.history.replaceState({}, '', cleanUrl);
        const scrollToCategory = () => {
          const catEl = document.getElementById(`cat-${cat}`);
          const target = catEl || document.getElementById('materials');
          if (target) {
            const navHeight = document.querySelector('nav')?.offsetHeight || 80;
            const top = target.getBoundingClientRect().top + window.scrollY - navHeight + 10;
            window.scrollTo({
              top,
              behavior: 'smooth'
            });
          }
          // Scroll selected product tab to the left edge of the carousel
          const tabContainer = catEl?.querySelector('.overflow-x-auto');
          if (tabContainer) {
            const prodIdx = prod ? parseInt(prod, 10) : 0;
            const buttons = tabContainer.querySelectorAll('button');
            if (buttons[prodIdx]) {
              const btn = buttons[prodIdx] as HTMLElement;
              const scrollOffset = btn.offsetLeft - 4;
              tabContainer.scrollTo({
                left: scrollOffset,
                behavior: 'smooth'
              });
            }
          }
        };
        // First scroll after expansion renders, second after animation settles
        setTimeout(scrollToCategory, 500);
        setTimeout(scrollToCategory, 900);
      }
    };
    // Check on mount (for cross-page navigation)
    handleMaterialsNav();
    // Listen for same-page navigation
    window.addEventListener('materialsNav', handleMaterialsNav);
    const handleCollapse = () => {
      setExpandedCategory(null);
    };
    const handleReset = () => {
      setExpandedCategory(null);
      setActiveTab('materials');
    };
    window.addEventListener('materialsCollapse', handleCollapse);
    window.addEventListener('materialsReset', handleReset);
    return () => {
      window.removeEventListener('materialsNav', handleMaterialsNav);
      window.removeEventListener('materialsCollapse', handleCollapse);
      window.removeEventListener('materialsReset', handleReset);
    };
  }, []);
  const handlePrev = (categoryName: string) => {
    const category = categories.find(cat => cat.name === categoryName);
    if (!category) return;
    setCarouselIndex(prev => prev === 0 ? category.products.length - 1 : prev - 1);
  };
  const handleNext = (categoryName: string) => {
    const category = categories.find(cat => cat.name === categoryName);
    if (!category) return;
    setCarouselIndex(prev => prev === category.products.length - 1 ? 0 : prev + 1);
  };
  const setDotIndex = (index: number) => {
    setCarouselIndex(index);
  };
  return <>
      <section className="relative py-24 dark:bg-[#242322] transition-colors duration-300 overflow-hidden border-t border-transparent dark:border-[#404040] bg-[#F3f2ed]" id="materials" data-id="element-1155">
        <div className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-b from-[#e8e0d2]/20 dark:from-[#3a3633]/20 to-transparent pointer-events-none" data-id="element-1156" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1157">
          {/* Header */}
          <div className="mb-20" data-id="element-1158">
            <motion.div initial={{
            width: 0,
            opacity: 0
          }} whileInView={{
            width: 64,
            opacity: 1
          }} viewport={{
            once: true
          }} transition={{
            width: {
              duration: 1.2,
              ease: [0.22, 1, 0.36, 1],
              delay: 1
            },
            opacity: {
              duration: 0.4,
              delay: 1
            }
          }} className="h-1 bg-brand-orange rounded-full mb-8" data-id="element-1159" />

            <div className="flex flex-col md:flex-row md:items-baseline gap-4 md:gap-8 mb-6" data-id="element-1160">
              <motion.h2 initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              type: 'spring',
              stiffness: 200,
              damping: 20,
              delay: 0.1
            }} className="font-heading text-5xl md:text-6xl lg:text-7xl font-extrabold text-brand-charcoal dark:text-white tracking-tight leading-[1.1]" data-id="element-1161">
                Materials <span className="text-brand-orange" data-id="element-1162">Hub</span>
              </motion.h2>

              <motion.span initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.15
            }} className="inline-block text-xs font-bold tracking-[0.2em] uppercase self-center text-[#1DB954]" data-id="element-1163">
                — Explore Our Catalog
              </motion.span>
            </div>

            <motion.p initial={{
            opacity: 0,
            y: 15
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 20,
            delay: 0.25
          }} className="text-brand-charcoal dark:text-white text-lg font-medium font-sub leading-relaxed max-w-lg tracking-wide" data-id="element-1164">
              Browse our catalog, estimate your needs, or find the right
              material for your next project.
            </motion.p>
          </div>

          {/* Tab Content */}
          <div className="min-h-[400px]" data-id="element-1165">
            <AnimatePresence mode="wait" data-id="element-1166">
              {/* MATERIALS TAB */}
              {activeTab === 'materials' && <motion.div key="materials" initial={{
              opacity: 0,
              y: 20,
              scale: 0.98
            }} animate={{
              opacity: 1,
              y: 0,
              scale: 1
            }} exit={{
              opacity: 0,
              y: -20,
              scale: 0.98
            }} transition={{
              type: 'spring',
              stiffness: 300,
              damping: 25
            }} className="space-y-0 divide-y divide-stone-200 dark:divide-[#4a4a4a]" data-id="element-1167">
                  {categories.map(category => {
                const isExpanded = expandedCategory === category.name;
                if (isExpanded) {
                  // Single-product carousel UI replacing the product grid
                  const product = category.products[carouselIndex] || null;
                  return <div key={category.name} id={`cat-${category.name}`} className="overflow-hidden scroll-mt-[135px]" data-id="element-1168">
                          <button onClick={() => {
                      if (isExpanded) {
                        setExpandedCategory(null);
                        setCategoryOverview(null);
                      } else {
                        setExpandedCategory(category.name);
                        setCategoryOverview(category.name);
                        setCarouselIndex(0);
                      }
                    }} className="w-full flex items-center justify-between py-3 sm:py-4 text-left focus:outline-none group" data-id="element-1169">
                            <div data-id="element-1170">
                              <h3 className={`font-heading text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight transition-colors duration-200 ${isExpanded ? 'text-brand-orange' : 'text-brand-charcoal dark:text-white/80 group-hover:text-brand-orange'}`} data-id="element-1171">
                                {category.name}
                              </h3>
                              <span className="text-[11px] font-normal text-stone-400 dark:text-white/40 tracking-wide" data-id="element-1172">
                                {category.name === 'Soil, Mulch & Org.' ? '15' : '12'}{' '}
                                yards max load
                              </span>
                            </div>
                            <ChevronDown className={`w-7 h-7 shrink-0 ml-4 transition-transform duration-300 ${isExpanded ? 'rotate-180 text-brand-orange' : 'text-brand-charcoal/30 dark:text-white/30 group-hover:text-brand-orange'}`} data-id="element-1173" />
                          </button>

                          <AnimatePresence mode="wait" data-id="element-1174">
                            {isExpanded && product && <motion.div initial={{
                        opacity: 0,
                        height: 0
                      }} animate={{
                        opacity: 1,
                        height: 'auto'
                      }} exit={{
                        opacity: 0,
                        height: 0
                      }} transition={{
                        duration: 0.3,
                        ease: 'easeInOut'
                      }} data-id="element-1175">
                                <div className="pb-10" data-id="element-1176">
                                  {/* Category Overview - Large Stacked Image Cards */}
                                  {categoryOverview === category.name ? <motion.div key="category-overview" initial={{
                            opacity: 0,
                            y: 10
                          }} animate={{
                            opacity: 1,
                            y: 0
                          }} exit={{
                            opacity: 0,
                            y: -10
                          }} transition={{
                            duration: 0.25
                          }} className="space-y-1.5" data-id="element-1177">
                                      {category.products.map((p, idx) => <motion.button key={p.slug} initial={{
                              opacity: 0,
                              y: 20
                            }} animate={{
                              opacity: 1,
                              y: 0
                            }} transition={{
                              duration: 0.3,
                              delay: idx * 0.06
                            }} onClick={() => {
                              setCategoryOverview(null);
                              setCarouselIndex(idx);
                              setCategoryGridView(null);
                              setTimeout(() => {
                                const catEl = document.getElementById(`cat-${category.name}`);
                                if (catEl) {
                                  const navHeight = document.querySelector('nav')?.offsetHeight || 80;
                                  const top = catEl.getBoundingClientRect().top + window.scrollY - navHeight - 20;
                                  window.scrollTo({
                                    top,
                                    behavior: 'smooth'
                                  });
                                }
                              }, 50);
                            }} className="w-full relative overflow-hidden rounded-xl group/overview block text-left" style={{
                              height: '200px'
                            }} data-id="element-1178">
                                          <img src={p.image} alt={p.name} className="absolute inset-0 w-full h-full object-cover group-hover/overview:scale-105 transition-transform duration-500" data-id="element-1179" />
                                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" data-id="element-1180" />
                                          {TOP_SELLER_SLUGS.has(p.slug) && <div className="absolute top-3 left-3 z-10" data-id="element-1181">
                                              <span className="inline-flex items-center gap-1.5 bg-amber-300 text-stone-900 text-xs sm:text-sm font-extrabold uppercase tracking-wider px-3 py-1.5 rounded-md shadow-md animate-shimmer" data-id="element-1182">
                                                <Star className="w-3.5 h-3.5 fill-stone-900" data-id="element-1183" />
                                                Top Seller
                                              </span>
                                            </div>}
                                          <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6" data-id="element-1184">
                                            <h4 className="font-heading text-2xl sm:text-3xl font-bold text-white leading-tight mb-1" data-id="element-1185">
                                              {p.name}
                                            </h4>
                                            <p className="text-white/90 text-sm sm:text-base font-medium" data-id="element-1186">
                                              ${p.pricePerYard} / yard
                                            </p>
                                          </div>
                                        </motion.button>)}
                                    </motion.div> : <>
                                      {/* Category Items Carousel */}
                                      <div className="mb-6 flex items-center gap-2" data-id="element-1187">
                                        <div className="relative flex-1 min-w-0" data-id="element-1188">
                                          <div className="overflow-x-auto scrollbar-hide -mx-1 px-1" data-carousel-category={category.name} data-id="element-1189">
                                            <div className="flex items-center gap-1 whitespace-nowrap pb-1" style={{
                                    scrollbarWidth: 'none',
                                    msOverflowStyle: 'none'
                                  }} data-id="element-1190">
                                              {category.products.map((p, idx) => <button key={p.name} data-chip-index={idx} onClick={e => {
                                      setCategoryGridView(null);
                                      setCarouselIndex(idx);
                                      setAddingToCart(null);
                                      // Center the clicked chip in the scroll container
                                      e.currentTarget.scrollIntoView({
                                        behavior: 'smooth',
                                        block: 'nearest',
                                        inline: 'center'
                                      });
                                    }} className={`relative shrink-0 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${categoryGridView === category.name ? 'text-stone-600 dark:text-stone-200 hover:text-white' : idx === carouselIndex ? 'text-brand-orange bg-brand-orange/10' : 'text-stone-600 dark:text-white/80 hover:text-white hover:bg-white/5'}`} data-id="element-1191">
                                                    {p.name}
                                                    {idx === carouselIndex && categoryGridView !== category.name && <motion.div layoutId="activeProduct" className="absolute bottom-0 left-2 right-2 h-[2px] bg-brand-orange rounded-full" transition={{
                                        type: 'spring',
                                        stiffness: 400,
                                        damping: 25
                                      }} data-id="element-1192" />}
                                                  </button>)}
                                            </div>
                                          </div>
                                        </div>
                                        <div className="shrink-0 border-l border-stone-300 dark:border-white/10 pl-3 ml-1" data-id="element-1193">
                                          <button onClick={() => setCategoryOverview(category.name)} className="px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-all bg-stone-200 dark:bg-white/10 text-stone-800 dark:text-stone-300 hover:bg-brand-orange/15 hover:text-brand-orange" data-id="element-1194">
                                            ← Back
                                          </button>
                                        </div>
                                      </div>

                                      {/* Category Grid View */}
                                      {categoryGridView === category.name ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 pb-4" data-id="element-1195">
                                          {category.products.map(p => <button key={p.slug} onClick={() => {
                                setCategoryGridView(null);
                                setCarouselIndex(category.products.findIndex(pr => pr.slug === p.slug));
                                setTimeout(() => {
                                  const catEl = document.getElementById(`cat-${category.name}`);
                                  if (catEl) {
                                    const navHeight = document.querySelector('nav')?.offsetHeight || 80;
                                    const top = catEl.getBoundingClientRect().top + window.scrollY - navHeight - 20;
                                    window.scrollTo({
                                      top,
                                      behavior: 'smooth'
                                    });
                                  }
                                }, 50);
                              }} className="bg-stone-200 dark:bg-[#282828] rounded-xl overflow-hidden border border-stone-400 dark:border-[#383838] hover:border-brand-orange/50 transition-all group/card text-left flex flex-col" data-id="element-1196">
                                              <div className="aspect-[4/3] overflow-hidden relative" data-id="element-1197">
                                                <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover/card:scale-105 transition-transform duration-500" data-id="element-1198" />
                                                <div className="absolute top-2 right-2 bg-black/70 backdrop-blur-sm text-white px-2.5 py-1 rounded-full text-xs font-bold" data-id="element-1199">
                                                  ${p.pricePerYard}/yd
                                                </div>
                                              </div>
                                              <div className="p-4" data-id="element-1200">
                                                <h4 className="font-bold text-brand-charcoal dark:text-white text-sm" data-id="element-1201">
                                                  {p.name}
                                                </h4>
                                                <p className="text-xs text-stone-600 dark:text-stone-200 mt-1 line-clamp-2" data-id="element-1202">
                                                  {p.description}
                                                </p>
                                              </div>
                                            </button>)}
                                        </div> : <div className="bg-[#faf8f6] dark:bg-[#353433] md:rounded-2xl md:border border-stone-200 dark:border-[#424242] overflow-hidden flex flex-col md:flex-row shadow-2xl md:shadow-[0_-8px_15px_-8px_rgba(255,255,255,0.08)] -mx-4 sm:mx-0 relative touch-pan-y" onTouchStart={e => {
                              const touch = e.touches[0];
                              (e.currentTarget as any)._swipeStartX = touch.clientX;
                              (e.currentTarget as any)._swipeStartY = touch.clientY;
                            }} onTouchEnd={e => {
                              const startX = (e.currentTarget as any)._swipeStartX;
                              const startY = (e.currentTarget as any)._swipeStartY;
                              if (startX == null || startY == null) return;
                              const touch = e.changedTouches[0];
                              const deltaX = touch.clientX - startX;
                              const deltaY = touch.clientY - startY;
                              // Only trigger if horizontal swipe is dominant and significant
                              if (Math.abs(deltaX) > 100 && Math.abs(deltaX) > Math.abs(deltaY) * 2) {
                                if (deltaX < 0) {
                                  handleNext(category.name);
                                } else {
                                  handlePrev(category.name);
                                }
                                // Scroll the active product tab into view
                                setTimeout(() => {
                                  const catEl = document.getElementById(`cat-${category.name}`);
                                  if (catEl) {
                                    const tabContainer = catEl.querySelector('.overflow-x-auto');
                                    if (tabContainer) {
                                      const activeBtn = tabContainer.querySelector('.text-brand-orange') as HTMLElement;
                                      if (activeBtn) {
                                        activeBtn.scrollIntoView({
                                          behavior: 'smooth',
                                          block: 'nearest',
                                          inline: 'nearest'
                                        });
                                      }
                                    }
                                  }
                                }, 50);
                              }
                              ;
                              (e.currentTarget as any)._swipeStartX = null;
                              (e.currentTarget as any)._swipeStartY = null;
                            }} data-id="element-1203">
                                          {/* Image — 2:3 ratio on mobile */}
                                          <div className="w-full md:w-1/2 aspect-[3/4] md:aspect-auto md:h-auto md:min-h-[400px] relative" data-id="element-1204">
                                            <ProductImageCarousel images={product.images || [product.image]} alt={product.name} data-id="element-1205" />
                                            {TOP_SELLER_SLUGS.has(product.slug) && <div className="absolute top-3 left-3 z-20" data-id="element-1206">
                                                <span className="inline-flex items-center gap-1.5 bg-amber-300 text-stone-900 text-xs sm:text-sm font-extrabold uppercase tracking-wider px-3 py-1.5 rounded-md shadow-md animate-shimmer" data-id="element-1207">
                                                  <Star className="w-3.5 h-3.5 fill-stone-900" data-id="element-1208" />
                                                  Top Seller
                                                </span>
                                              </div>}
                                            {/* Gradient fade for mobile with overlaid product name */}
                                            <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-t from-[#faf8f6] dark:from-[#353433] via-[#faf8f6]/60 dark:via-[#353433]/60 to-transparent pointer-events-none md:hidden" data-id="element-1209" />
                                            <div className="absolute bottom-0 left-0 right-0 p-6 z-10 md:hidden" data-id="element-1210">
                                              <span className="text-xs font-bold text-brand-muted uppercase tracking-wider mb-1 block drop-shadow-md" data-id="element-1211">
                                                {category.name}
                                              </span>
                                              <h4 className="font-heading text-3xl font-bold text-brand-charcoal dark:text-white drop-shadow-md" data-id="element-1212">
                                                {product.name}
                                              </h4>
                                            </div>
                                          </div>

                                          {/* Details */}
                                          <div className="w-full md:w-1/2 p-6 sm:p-8 md:p-10 flex flex-col relative z-10 -mt-6 md:mt-0" data-id="element-1213">
                                            <span className="hidden md:block text-xs font-bold text-brand-muted uppercase tracking-wider mb-2" data-id="element-1214">
                                              {category.name}
                                            </span>
                                            <h4 className="hidden md:block font-heading text-3xl md:text-4xl font-bold text-brand-charcoal dark:text-white mb-4" data-id="element-1215">
                                              {product.name}
                                            </h4>
                                            {/* Tabbed Info */}
                                            {(() => {
                                  const tab = getProductTab(product.slug);
                                  const tabs = [{
                                    id: 'overview' as const,
                                    label: 'Overview'
                                  }, {
                                    id: 'specs' as const,
                                    label: 'Specs'
                                  }, {
                                    id: 'docs' as const,
                                    label: 'Documents'
                                  }];
                                  return <div className="mb-5" data-id="element-1216">
                                                  <div className="flex border-b border-stone-200 dark:border-[#444] mb-4" data-id="element-1217">
                                                    {tabs.map(tb => <button key={tb.id} type="button" onClick={() => setProductTab(product.slug, tb.id)} className={`pb-2.5 px-3 text-sm font-bold transition-colors relative ${tab === tb.id ? 'text-brand-orange' : 'text-stone-500 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200'}`} data-id="element-1218">
                                                        {tb.label}
                                                        {tab === tb.id && <motion.div layoutId={`tab-${product.slug}`} className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-orange" data-id="element-1219" />}
                                                      </button>)}
                                                  </div>
                                                  <div className="min-h-[120px]" data-id="element-1220">
                                                    <AnimatePresence mode="wait" data-id="element-1221">
                                                      {tab === 'overview' && <motion.div key="overview" initial={{
                                          opacity: 0,
                                          y: 6
                                        }} animate={{
                                          opacity: 1,
                                          y: 0
                                        }} exit={{
                                          opacity: 0,
                                          y: -6
                                        }} transition={{
                                          duration: 0.18
                                        }} data-id="element-1222">
                                                          <p className="text-brand-charcoal/80 dark:text-white/90 text-base leading-relaxed mb-4" data-id="element-1223">
                                                            {getProductDescription(product, locale)}
                                                          </p>
                                                          {product.bestFor && product.bestFor.length > 0 && <div className="mb-4" data-id="element-1224">
                                                                <p className="text-[10px] font-bold text-stone-500 dark:text-stone-400 uppercase tracking-wider mb-2" data-id="element-1225">
                                                                  Best For
                                                                </p>
                                                                <div className="flex flex-wrap gap-1.5" data-id="element-1226">
                                                                  {product.bestFor.map((tag, idx) => <span key={idx} className="inline-flex items-center px-3 py-1 rounded-full bg-brand-lightgray dark:bg-[#2a2a2a] text-xs font-medium text-brand-charcoal dark:text-stone-300 border border-stone-200 dark:border-[#444444]" data-id="element-1227">
                                                                        {tag}
                                                                      </span>)}
                                                                </div>
                                                              </div>}
                                                          {product.proTip && <div className="bg-brand-orange/10 border border-brand-orange/20 p-3 rounded-lg flex items-start gap-2" data-id="element-1228">
                                                              <Info className="w-3.5 h-3.5 text-brand-orange mt-0.5 shrink-0" data-id="element-1229" />
                                                              <p className="text-xs text-brand-charcoal/80 dark:text-white/80 leading-relaxed" data-id="element-1230">
                                                                <span className="font-bold" data-id="element-1231">
                                                                  Pro Tip:
                                                                </span>{' '}
                                                                {product.proTip}
                                                              </p>
                                                            </div>}
                                                        </motion.div>}
                                                      {tab === 'specs' && <motion.div key="specs" initial={{
                                          opacity: 0,
                                          y: 6
                                        }} animate={{
                                          opacity: 1,
                                          y: 0
                                        }} exit={{
                                          opacity: 0,
                                          y: -6
                                        }} transition={{
                                          duration: 0.18
                                        }} data-id="element-1232">
                                                          {(product as any).specs ? <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-1233">
                                                              {Object.entries((product as any).specs).map(([key, value]) => {
                                              const labels: Record<string, string> = {
                                                size: 'Size / Gradation',
                                                weightPerYard: 'Density (Weight/Yd)',
                                                compaction: 'Compaction',
                                                coverage: 'Est. Coverage'
                                              };
                                              return <div key={key} className="bg-stone-50 dark:bg-[#222] p-3 rounded-lg border border-stone-200 dark:border-[#333]" data-id="element-1234">
                                                                      <p className="text-[10px] font-bold text-stone-500 dark:text-stone-400 uppercase tracking-wider mb-1" data-id="element-1235">
                                                                        {labels[key] || key}
                                                                      </p>
                                                                      <p className="text-sm font-medium text-stone-900 dark:text-stone-200" data-id="element-1236">
                                                                        {String(value)}
                                                                      </p>
                                                                    </div>;
                                            })}
                                                            </div> : <p className="text-sm text-stone-500 dark:text-stone-400 py-4" data-id="element-1237">
                                                              No detailed
                                                              specifications
                                                              available. Call
                                                              (206) 619-3742 for
                                                              tech data.
                                                            </p>}
                                                        </motion.div>}
                                                      {tab === 'docs' && <motion.div key="docs" initial={{
                                          opacity: 0,
                                          y: 6
                                        }} animate={{
                                          opacity: 1,
                                          y: 0
                                        }} exit={{
                                          opacity: 0,
                                          y: -6
                                        }} transition={{
                                          duration: 0.18
                                        }} className="space-y-2" data-id="element-1238">
                                                          <button onClick={() => generateSpecSheet({
                                            product,
                                            categoryName: category.name
                                          })} className="w-full flex items-center gap-3 p-3 rounded-lg border border-brand-orange/30 bg-brand-orange/5 hover:bg-brand-orange/10 hover:border-brand-orange/50 transition-all text-left" data-id="element-1239">
                                                            <div className="w-9 h-9 rounded-md bg-brand-orange/15 flex items-center justify-center shrink-0" data-id="element-1240">
                                                              <FileText className="w-4 h-4 text-brand-orange" data-id="element-1241" />
                                                            </div>
                                                            <div className="min-w-0 flex-1" data-id="element-1242">
                                                              <p className="text-sm font-bold text-stone-900 dark:text-white truncate" data-id="element-1243">
                                                                Product Spec
                                                                Sheet
                                                              </p>
                                                              <p className="text-xs text-stone-500 dark:text-stone-400 truncate" data-id="element-1244">
                                                                Specs, coverage,
                                                                pricing
                                                              </p>
                                                            </div>
                                                            <span className="inline-flex items-center gap-1 text-xs font-bold text-brand-orange shrink-0" data-id="element-1245">
                                                              <Download className="w-3.5 h-3.5" data-id="element-1246" />
                                                            </span>
                                                          </button>
                                                          <div className="flex items-start gap-3 p-3 rounded-lg border border-stone-200 dark:border-[#444] bg-stone-50 dark:bg-[#2a2a2a]" data-id="element-1247">
                                                            <div className="w-9 h-9 rounded-md bg-stone-100 dark:bg-[#333] flex items-center justify-center shrink-0" data-id="element-1248">
                                                              <Mail className="w-3.5 h-3.5 text-stone-500 dark:text-stone-400" data-id="element-1249" />
                                                            </div>
                                                            <p className="text-xs text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-1250">
                                                              Need a gradation
                                                              curve, MSDS, or
                                                              compaction test?{' '}
                                                              <a href={`mailto:info@253gravel.com?subject=${encodeURIComponent(`Certified docs request: ${product.name}`)}`} className="text-brand-orange font-semibold hover:underline break-all" data-id="element-1251">
                                                                info@253gravel.com
                                                              </a>
                                                              .
                                                            </p>
                                                          </div>
                                                        </motion.div>}
                                                    </AnimatePresence>
                                                  </div>
                                                </div>;
                                })()}

                                            {category.name !== 'Specialty Rock' && product.pricePerYard > 0 ? <>
                                                <div className="mb-6 pb-6 border-b border-stone-200/50 dark:border-[#4a4a4a]/50" data-id="element-1252">
                                                  {(() => {
                                      const isBarkMulch = isLightMaterial(product.name);
                                      const qtyNum = selectedQuantity;
                                      const fullLoadYards = isBarkMulch ? 15 : 12;
                                      const minChargeYards = 3;
                                      const smallLoadThreshold = isBarkMulch ? 12 : 10;
                                      // Shared sub-5yd curve:
                                      // ≥threshold → 1×, 5..threshold
                                      // → 10%/yd step, 3..4 →
                                      // continues at 15%/yd step.
                                      const computeSurcharge = (y: number) => {
                                        if (y >= smallLoadThreshold) return 1;
                                        if (y >= 5) return 1 + (smallLoadThreshold - y) * 0.1;
                                        const at5 = 1 + (smallLoadThreshold - 5) * 0.1;
                                        return at5 + (5 - y) * 0.15;
                                      };
                                      // Helper: calculate total for a single-load quantity
                                      const calcLoadTotal = (yards: number) => {
                                        const billableYards = Math.max(yards, minChargeYards);
                                        const surcharge = computeSurcharge(billableYards);
                                        const price = Math.ceil(product.pricePerYard * surcharge);
                                        return billableYards * price;
                                      };
                                      // Helper: get effective per-yard price for display
                                      const calcEffectivePrice = (yards: number) => {
                                        const billableYards = Math.max(yards, minChargeYards);
                                        const surcharge = computeSurcharge(billableYards);
                                        return Math.ceil(product.pricePerYard * surcharge);
                                      };
                                      const isBeyondFullLoad = qtyNum > fullLoadYards;
                                      const isBelowMinCharge = qtyNum < minChargeYards;
                                      const displayYards = isBeyondFullLoad ? fullLoadYards : qtyNum;
                                      const effectivePrice = calcEffectivePrice(displayYards);
                                      const isSmallLoad = !isBeyondFullLoad && qtyNum < smallLoadThreshold;
                                      // Tiered bulk discount: light materials 5% at 15yds, others 5% at 24yds; 10% at 4 full loads
                                      const bulkDiscountPct = qtyNum >= fullLoadYards * 4 ? 0.1 : qtyNum >= (isBarkMulch ? fullLoadYards : fullLoadYards * 2) ? 0.05 : 0;
                                      const hasBulkDiscount = bulkDiscountPct > 0;
                                      // Build delivery loads
                                      const deliveryLoads: {
                                        yards: number;
                                        total: number;
                                      }[] = [];
                                      if (isBeyondFullLoad) {
                                        let remaining = qtyNum;
                                        while (remaining > 0) {
                                          const loadYards = Math.min(remaining, fullLoadYards);
                                          deliveryLoads.push({
                                            yards: loadYards,
                                            total: calcLoadTotal(loadYards)
                                          });
                                          remaining -= loadYards;
                                        }
                                      }
                                      let total = isBeyondFullLoad ? deliveryLoads.reduce((sum, l) => sum + l.total, 0) : calcLoadTotal(qtyNum);
                                      // Apply tiered bulk discount
                                      const preDiscountTotal = total;
                                      if (hasBulkDiscount) {
                                        total = Math.round(total * (1 - bulkDiscountPct));
                                        deliveryLoads.forEach((l, i) => {
                                          deliveryLoads[i] = {
                                            ...l,
                                            total: Math.round(l.total * (1 - bulkDiscountPct))
                                          };
                                        });
                                      }
                                      const extraYards = isBeyondFullLoad ? qtyNum - fullLoadYards : 0;
                                      const extraBillable = Math.max(extraYards, minChargeYards);
                                      const extraEffectivePrice = isBeyondFullLoad ? calcEffectivePrice(extraYards) : 0;
                                      const hasMessages = hasBulkDiscount || isBelowMinCharge || isSmallLoad || isBeyondFullLoad;
                                      return <div className="bg-stone-100/70 dark:bg-black/20 backdrop-blur-md rounded-xl p-4 sm:p-5 border border-stone-200 dark:border-white/5 shadow-sm" data-id="element-1253">
                                                        <div className="flex items-center justify-between gap-3" data-id="element-1254">
                                                          <div className="flex items-baseline gap-1" data-id="element-1255">
                                                            <span className="font-heading text-3xl sm:text-4xl font-bold text-brand-charcoal dark:text-white" data-id="element-1256">
                                                              $
                                                              {hasBulkDiscount ? Math.ceil(effectivePrice * (1 - bulkDiscountPct)) : effectivePrice}
                                                            </span>
                                                            <span className="text-brand-charcoal/60 dark:text-white/60 text-sm font-medium" data-id="element-1257">
                                                              /yd
                                                            </span>
                                                          </div>

                                                          <div className="flex items-baseline gap-1.5 text-right" data-id="element-1258">
                                                            <span className="text-brand-charcoal/80 dark:text-white/80 text-sm font-medium" data-id="element-1259">
                                                              {qtyNum} yds =
                                                            </span>
                                                            <span className="font-heading font-bold text-brand-charcoal dark:text-white text-xl" data-id="element-1260">
                                                              ${total}
                                                            </span>
                                                            <span className="text-brand-charcoal/70 dark:text-white/70 text-sm" data-id="element-1261">
                                                              +del
                                                            </span>
                                                          </div>
                                                        </div>

                                                        {hasMessages && <div className="mt-4 pt-3 border-t border-brand-charcoal/5 dark:border-white/5 space-y-1.5" data-id="element-1262">
                                                            {hasBulkDiscount && <div className="flex items-center justify-between" data-id="element-1263">
                                                                <p className="text-xs text-green-600 dark:text-green-400 font-semibold" data-id="element-1264">
                                                                  {Math.round(bulkDiscountPct * 100)}
                                                                  % bulk
                                                                  discount
                                                                  applied (
                                                                  {qtyNum >= 48 ? '48' : '24'}
                                                                  + yards)
                                                                </p>
                                                                <p className="text-xs text-brand-charcoal/40 dark:text-white/40 line-through" data-id="element-1265">
                                                                  Was $
                                                                  {preDiscountTotal}
                                                                </p>
                                                              </div>}
                                                            {!isBeyondFullLoad && isBelowMinCharge && <p className="text-xs text-brand-orange" data-id="element-1266">
                                                                  {minChargeYards}{' '}
                                                                  yard minimum
                                                                  charge applies
                                                                </p>}
                                                            {isSmallLoad && !isBelowMinCharge && <p className="text-xs text-brand-orange" data-id="element-1267">
                                                                  Small load
                                                                  upcharge · $
                                                                  {product.pricePerYard}
                                                                  /yd at{' '}
                                                                  {smallLoadThreshold}
                                                                  + yards
                                                                </p>}
                                                            {isBeyondFullLoad && deliveryLoads.length > 0 && <div className="space-y-1" data-id="element-1268">
                                                                  {deliveryLoads.map((load, idx) => {
                                              const loadBillable = Math.max(load.yards, minChargeYards);
                                              const showCharged = load.yards < loadBillable;
                                              return <div key={idx} className="flex justify-between text-xs text-brand-charcoal/70 dark:text-white/70" data-id="element-1269">
                                                                          <span data-id="element-1270">
                                                                            Delivery{' '}
                                                                            {idx + 1}
                                                                            :{' '}
                                                                            {load.yards}{' '}
                                                                            yds
                                                                            {showCharged && ` (charged as ${loadBillable})`}
                                                                          </span>
                                                                          <span className="font-bold text-brand-charcoal dark:text-white" data-id="element-1271">
                                                                            $
                                                                            {load.total}
                                                                          </span>
                                                                        </div>;
                                            })}
                                                                  {deliveryLoads.length > 1 && <p className="text-xs text-brand-orange mt-1" data-id="element-1272">
                                                                      +
                                                                      additional
                                                                      delivery
                                                                      fee for{' '}
                                                                      {deliveryLoads.length - 1}{' '}
                                                                      extra{' '}
                                                                      {deliveryLoads.length > 2 ? 'loads' : 'load'}
                                                                    </p>}
                                                                </div>}
                                                          </div>}
                                                      </div>;
                                    })()}
                                                </div>

                                                <div className="space-y-4 mt-auto" data-id="element-1273">
                                                  {addedFlash === product.name ? <motion.div initial={{
                                      opacity: 0,
                                      y: 10
                                    }} animate={{
                                      opacity: 1,
                                      y: 0
                                    }} className="space-y-3" data-id="element-1274">
                                                      <div className="flex items-center justify-center w-full py-3 bg-green-500/10 dark:bg-green-500/20 text-green-600 dark:text-green-400 font-bold text-sm border border-green-500/30 rounded-xl" data-id="element-1275">
                                                        <Check className="w-4 h-4 mr-2" data-id="element-1276" />
                                                        Added to Truck!
                                                      </div>
                                                      <div className="flex gap-2" data-id="element-1277">
                                                        <button onClick={() => {
                                          setAddedFlash(null);
                                          setExpandedCategory(null);
                                          setCategoryOverview(null);
                                          setTimeout(() => {
                                            const el = document.getElementById('materials');
                                            if (el) {
                                              const navHeight = document.querySelector('nav')?.offsetHeight || 80;
                                              const top = el.getBoundingClientRect().top + window.scrollY - navHeight;
                                              window.scrollTo({
                                                top,
                                                behavior: 'smooth'
                                              });
                                            }
                                          }, 100);
                                        }} className="flex-1 py-2.5 rounded-xl text-sm font-bold text-brand-charcoal dark:text-white border border-stone-200 dark:border-white/10 bg-white/50 dark:bg-white/5 hover:bg-white/80 dark:hover:bg-white/10 transition-colors" data-id="element-1278">
                                                          Continue Shopping
                                                        </button>
                                                        <button onClick={() => {
                                          setAddedFlash(null);
                                          openCart();
                                        }} className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white bg-brand-orange hover:bg-brand-orangeHover transition-colors flex items-center justify-center gap-1.5" data-id="element-1279">
                                                          <ShoppingCart className="w-4 h-4" data-id="element-1280" />
                                                          View Cart
                                                        </button>
                                                      </div>
                                                    </motion.div> : <>
                                                      <div data-id="element-1281">
                                                        <div className="flex items-center justify-between gap-3 flex-wrap mb-2" data-id="element-1282">
                                                          <span className="block text-sm font-bold text-brand-charcoal dark:text-white" data-id="element-1283">
                                                            Select Quantity
                                                          </span>
                                                          {(() => {
                                            const isLight = isLightMaterial(product.name);
                                            const fullLoad = isLight ? 15 : 12;
                                            const tier2Yards = isLight ? fullLoad : fullLoad * 2;
                                            const tier3Yards = fullLoad * 4;
                                            return <span className="text-[11px] text-brand-muted dark:text-stone-400" data-id="element-1284">
                                                                5% at{' '}
                                                                {tier2Yards} yd
                                                                ·{' '}
                                                                <span className="font-semibold text-[#AEB9B2] dark:text-[#6FB389]" data-id="element-1285">
                                                                  10% at{' '}
                                                                  {tier3Yards}{' '}
                                                                  yd
                                                                </span>
                                                              </span>;
                                          })()}
                                                        </div>

                                                        <div className="space-y-3" data-id="element-1286">
                                                          {/* Segmented intent picker — replaces the +/- stepper + dropdown popup so the three intents (less than full, full load, multiple loads) are equally discoverable. */}
                                                          {(() => {
                                            const isLight = isLightMaterial(product.name);
                                            const fullLoad = isLight ? 15 : 12;
                                            const segment: 'less' | 'full' | 'multi' = selectedQuantity > fullLoad ? 'multi' : selectedQuantity === fullLoad ? 'full' : 'less';
                                            const handleSegment = (s: 'less' | 'full' | 'multi') => {
                                              if (s === 'full') {
                                                setSelectedQuantity(fullLoad);
                                              } else if (s === 'multi') {
                                                setSelectedQuantity(fullLoad * 2);
                                              } else {
                                                const isCurrentLess = selectedQuantity < fullLoad;
                                                setSelectedQuantity(isCurrentLess ? selectedQuantity : 5);
                                              }
                                            };
                                            const lessYards = isLight ? [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14] : [3, 4, 5, 6, 7, 8, 9, 10, 11];
                                            const loadOptions = [{
                                              loads: 2,
                                              yards: fullLoad * 2
                                            }, {
                                              loads: 3,
                                              yards: fullLoad * 3
                                            }, {
                                              loads: 4,
                                              yards: fullLoad * 4
                                            }];
                                            const segments = [{
                                              id: 'less' as const,
                                              label: 'Less than full',
                                              sub: `${lessYards[0]}–${lessYards[lessYards.length - 1]} yd`
                                            }, {
                                              id: 'full' as const,
                                              label: 'Full Load',
                                              sub: `${fullLoad} yd`,
                                              recommended: true
                                            }, {
                                              id: 'multi' as const,
                                              label: 'Multiple loads',
                                              sub: `${fullLoad * 2}+ yd`
                                            }];
                                            return <div className="space-y-2" data-id="element-1287">
                                                                <div role="radiogroup" aria-label="How much do you need?" className="grid grid-cols-3 gap-1 p-1 bg-stone-100 dark:bg-white/5 border border-stone-200 dark:border-white/10 rounded-xl" data-id="element-1288">
                                                                  {segments.map(seg => {
                                                  const isActive = segment === seg.id;
                                                  return <button key={seg.id} type="button" role="radio" aria-checked={isActive} onClick={() => handleSegment(seg.id)} className={`relative px-1.5 py-2.5 rounded-lg transition-all text-center ${isActive ? 'bg-[#1DB954] text-white shadow-md' : 'text-brand-charcoal dark:text-white/80 hover:bg-stone-200 dark:hover:bg-white/10'}`} data-id="element-1289">
                                                                          {seg.recommended && !isActive && <span className="absolute -top-1.5 left-1/2 -translate-x-1/2 text-[8px] font-extrabold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#1DB954] text-white whitespace-nowrap" data-id="element-1290">
                                                                                Most
                                                                                picked
                                                                              </span>}
                                                                          <div className={`text-[11px] sm:text-xs leading-tight ${isActive ? 'font-extrabold' : 'font-bold'}`} data-id="element-1291">
                                                                            {seg.label}
                                                                          </div>
                                                                          <div className={`text-[10px] mt-0.5 ${isActive ? 'text-white/90' : 'text-brand-muted dark:text-white/50'}`} data-id="element-1292">
                                                                            {seg.sub}
                                                                          </div>
                                                                        </button>;
                                                })}
                                                                </div>

                                                                <AnimatePresence mode="wait" data-id="element-1293">
                                                                  {segment === 'less' && <motion.div key="less" initial={{
                                                  opacity: 0,
                                                  height: 0
                                                }} animate={{
                                                  opacity: 1,
                                                  height: 'auto'
                                                }} exit={{
                                                  opacity: 0,
                                                  height: 0
                                                }} transition={{
                                                  duration: 0.2
                                                }} className="overflow-hidden" data-id="element-1294">
                                                                      <div className="flex flex-wrap gap-1.5 p-2.5 rounded-xl bg-stone-100 dark:bg-white/5 border border-stone-200 dark:border-white/10" data-id="element-1295">
                                                                        {lessYards.map(y => {
                                                      const selected = selectedQuantity === y;
                                                      return <button key={y} type="button" onClick={() => setSelectedQuantity(y)} className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all ${selected ? 'bg-[#1DB954] text-white shadow-sm' : 'bg-white dark:bg-white/5 text-brand-charcoal dark:text-white/80 border border-stone-200 dark:border-white/10 hover:bg-stone-50 dark:hover:bg-white/10'}`} data-id="element-1296">
                                                                                {y}{' '}
                                                                                yd
                                                                                {y === 3 && <span className={`ml-1 text-[9px] font-extrabold uppercase tracking-wider ${selected ? 'text-white/90' : 'text-[#1DB954]'}`} data-id="element-1297">
                                                                                    Min
                                                                                  </span>}
                                                                              </button>;
                                                    })}
                                                                      </div>
                                                                    </motion.div>}
                                                                  {segment === 'multi' && <motion.div key="multi" initial={{
                                                  opacity: 0,
                                                  height: 0
                                                }} animate={{
                                                  opacity: 1,
                                                  height: 'auto'
                                                }} exit={{
                                                  opacity: 0,
                                                  height: 0
                                                }} transition={{
                                                  duration: 0.2
                                                }} className="overflow-hidden" data-id="element-1298">
                                                                      <div className="flex flex-wrap gap-1.5 p-2.5 rounded-xl bg-stone-100 dark:bg-white/5 border border-stone-200 dark:border-white/10" data-id="element-1299">
                                                                        {loadOptions.map(opt => {
                                                      const selected = selectedQuantity === opt.yards;
                                                      return <button key={opt.loads} type="button" onClick={() => setSelectedQuantity(opt.yards)} className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all ${selected ? 'bg-[#1DB954] text-white shadow-sm' : 'bg-white dark:bg-white/5 text-brand-charcoal dark:text-white/80 border border-stone-200 dark:border-white/10 hover:bg-stone-50 dark:hover:bg-white/10'}`} data-id="element-1300">
                                                                                {opt.loads}{' '}
                                                                                loads
                                                                                <span className={`ml-1 ${selected ? 'text-white/80' : 'text-brand-muted dark:text-white/50'}`} data-id="element-1301">
                                                                                  (
                                                                                  {opt.yards}{' '}
                                                                                  yd)
                                                                                </span>
                                                                              </button>;
                                                    })}
                                                                        <button type="button" onClick={() => setIsQuoteModalOpen(true)} className="px-3 py-1.5 rounded-md text-xs font-bold bg-white dark:bg-white/5 text-brand-charcoal dark:text-white/80 border border-stone-200 dark:border-white/10 hover:bg-stone-50 dark:hover:bg-white/10 transition-all" data-id="element-1302">
                                                                          5+
                                                                          loads
                                                                          <span className="ml-1 text-brand-muted dark:text-white/50" data-id="element-1303">
                                                                            (custom
                                                                            quote)
                                                                          </span>
                                                                        </button>
                                                                      </div>
                                                                    </motion.div>}
                                                                  {segment === 'full' && <motion.p key="full" initial={{
                                                  opacity: 0,
                                                  height: 0
                                                }} animate={{
                                                  opacity: 1,
                                                  height: 'auto'
                                                }} exit={{
                                                  opacity: 0,
                                                  height: 0
                                                }} transition={{
                                                  duration: 0.2
                                                }} className="overflow-hidden text-[11px] text-brand-muted dark:text-white/60 pl-1" data-id="element-1304">
                                                                      One full
                                                                      truckload
                                                                      (
                                                                      {fullLoad}{' '}
                                                                      yd)
                                                                      delivered
                                                                      in a
                                                                      single
                                                                      trip —
                                                                      best
                                                                      per-yard
                                                                      value.
                                                                    </motion.p>}
                                                                </AnimatePresence>
                                                              </div>;
                                          })()}

                                                          <button onClick={() => handleConfirmAdd(category.name, product.name, product.pricePerYard)} className="w-full py-3 rounded-none font-bold transition-all duration-300 flex items-center justify-center bg-brand-button hover:bg-brand-buttonHover text-white shadow-[0_0_12px_rgba(5,137,77,0.4)]" data-id="element-1305">
                                                            <Truck className="w-5 h-5 mr-2" data-id="element-1306" />
                                                            Add to Truck
                                                          </button>
                                                        </div>
                                                        <div className="mt-3 flex justify-center" data-id="element-1307">
                                                          <SameDayBadge variant="full" data-id="element-1308" />
                                                        </div>
                                                        {/* Small-load minimum warning (shown when the selected qty
                                                         triggers the 5yd/load minimum on a partial last load). */}
                                                        <SmallLoadBreakdown quantity={selectedQuantity} productName={product.name} className="mt-3" data-id="element-1309" />
                                                      </div>

                                                      {/* Inline Calculator */}
                                                      <div className="mt-4" data-id="element-1310">
                                                        <button onClick={() => setShowInlineCalc(!showInlineCalc)} className="w-full flex items-center justify-center gap-2 py-2 text-sm font-medium text-brand-orange hover:text-brand-orangeHover transition-colors" data-id="element-1311">
                                                          <Calculator className="w-4 h-4" data-id="element-1312" />
                                                          {showInlineCalc ? 'Hide Calculator' : 'Calculate how much I need'}
                                                        </button>

                                                        <AnimatePresence data-id="element-1313">
                                                          {showInlineCalc && <motion.div initial={{
                                            opacity: 0,
                                            height: 0
                                          }} animate={{
                                            opacity: 1,
                                            height: 'auto'
                                          }} exit={{
                                            opacity: 0,
                                            height: 0
                                          }} className="overflow-hidden" data-id="element-1314">
                                                              <div className="p-4 mt-2 bg-stone-50 dark:bg-[#303030] border border-stone-200 dark:border-[#4b4b4b] rounded-xl space-y-4" data-id="element-1315">
                                                                {/* Shape Selector */}
                                                                <div data-id="element-1316">
                                                                  <label className="block text-[11px] font-bold text-stone-600 dark:text-stone-300 uppercase tracking-wider mb-2" data-id="element-1317">
                                                                    Area Shape
                                                                  </label>
                                                                  <div className="grid grid-cols-4 gap-1.5" data-id="element-1318">
                                                                    {([{
                                                    id: 'rectangle',
                                                    label: 'Rect'
                                                  }, {
                                                    id: 'circle',
                                                    label: 'Circle'
                                                  }, {
                                                    id: 'triangle',
                                                    label: 'Tri'
                                                  }, {
                                                    id: 'irregular',
                                                    label: 'Other'
                                                  }] as const).map(s => <button key={s.id} onClick={() => setCalcShape(s.id)} className={`py-2 rounded-lg text-xs font-bold transition-colors ${calcShape === s.id ? 'bg-brand-orange text-white' : 'bg-white dark:bg-[#252525] text-stone-700 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-[#2f2f2f] border border-stone-200 dark:border-[#404040]'}`} data-id="element-1319">
                                                                          {s.label}
                                                                        </button>)}
                                                                  </div>
                                                                </div>

                                                                {/* Unit Toggles */}
                                                                <div className="flex items-center gap-3 text-[11px]" data-id="element-1320">
                                                                  <span className="font-medium text-stone-500 dark:text-stone-400" data-id="element-1321">
                                                                    Units:
                                                                  </span>
                                                                  <div className="inline-flex rounded-md border border-stone-200 dark:border-[#404040] overflow-hidden" data-id="element-1322">
                                                                    {(['ft', 'm'] as const).map(u => <button key={u} onClick={() => setCalcUnit(u)} className={`px-2.5 py-1 font-bold transition-colors ${calcUnit === u ? 'bg-brand-orange text-white' : 'bg-white dark:bg-[#252525] text-stone-600 dark:text-stone-400'}`} data-id="element-1323">
                                                                          {u}
                                                                        </button>)}
                                                                  </div>
                                                                  <span className="font-medium text-stone-500 dark:text-stone-400 ml-2" data-id="element-1324">
                                                                    Depth:
                                                                  </span>
                                                                  <div className="inline-flex rounded-md border border-stone-200 dark:border-[#404040] overflow-hidden" data-id="element-1325">
                                                                    {(['in', 'cm'] as const).map(u => <button key={u} onClick={() => setCalcDepthUnit(u)} className={`px-2.5 py-1 font-bold transition-colors ${calcDepthUnit === u ? 'bg-brand-orange text-white' : 'bg-white dark:bg-[#252525] text-stone-600 dark:text-stone-400'}`} data-id="element-1326">
                                                                          {u}
                                                                        </button>)}
                                                                  </div>
                                                                </div>

                                                                {/* Dimension Inputs (per shape) */}
                                                                {calcShape === 'rectangle' || calcShape === 'irregular' ? <div className="grid grid-cols-3 gap-3" data-id="element-1327">
                                                                    <div data-id="element-1328">
                                                                      <label className="block text-xs font-medium text-stone-600 dark:text-stone-300 mb-1" data-id="element-1329">
                                                                        Length (
                                                                        {calcUnit}
                                                                        )
                                                                      </label>
                                                                      <input type="number" value={inlineCalcLength} onChange={e => setInlineCalcLength(e.target.value)} placeholder="0" className="w-full px-3 py-2 bg-white dark:bg-[#252525] border border-stone-300 dark:border-[#404040] rounded-lg text-sm text-stone-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent" min="0" step="any" data-id="element-1330" />
                                                                    </div>
                                                                    <div data-id="element-1331">
                                                                      <label className="block text-xs font-medium text-stone-600 dark:text-stone-300 mb-1" data-id="element-1332">
                                                                        Width (
                                                                        {calcUnit}
                                                                        )
                                                                      </label>
                                                                      <input type="number" value={inlineCalcWidth} onChange={e => setInlineCalcWidth(e.target.value)} placeholder="0" className="w-full px-3 py-2 bg-white dark:bg-[#252525] border border-stone-300 dark:border-[#404040] rounded-lg text-sm text-stone-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent" min="0" step="any" data-id="element-1333" />
                                                                    </div>
                                                                    <div data-id="element-1334">
                                                                      <label className="block text-xs font-medium text-stone-600 dark:text-stone-300 mb-1" data-id="element-1335">
                                                                        Depth (
                                                                        {calcDepthUnit}
                                                                        )
                                                                      </label>
                                                                      <input type="number" value={inlineCalcDepth} onChange={e => setInlineCalcDepth(e.target.value)} placeholder="3" className="w-full px-3 py-2 bg-white dark:bg-[#252525] border border-stone-300 dark:border-[#404040] rounded-lg text-sm text-stone-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent" min="0" step="any" data-id="element-1336" />
                                                                    </div>
                                                                  </div> : calcShape === 'circle' ? <div className="grid grid-cols-2 gap-3" data-id="element-1337">
                                                                    <div data-id="element-1338">
                                                                      <label className="block text-xs font-medium text-stone-600 dark:text-stone-300 mb-1" data-id="element-1339">
                                                                        Diameter
                                                                        (
                                                                        {calcUnit}
                                                                        )
                                                                      </label>
                                                                      <input type="number" value={inlineCalcDiameter} onChange={e => setInlineCalcDiameter(e.target.value)} placeholder="0" className="w-full px-3 py-2 bg-white dark:bg-[#252525] border border-stone-300 dark:border-[#404040] rounded-lg text-sm text-stone-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent" min="0" step="any" data-id="element-1340" />
                                                                    </div>
                                                                    <div data-id="element-1341">
                                                                      <label className="block text-xs font-medium text-stone-600 dark:text-stone-300 mb-1" data-id="element-1342">
                                                                        Depth (
                                                                        {calcDepthUnit}
                                                                        )
                                                                      </label>
                                                                      <input type="number" value={inlineCalcDepth} onChange={e => setInlineCalcDepth(e.target.value)} placeholder="3" className="w-full px-3 py-2 bg-white dark:bg-[#252525] border border-stone-300 dark:border-[#404040] rounded-lg text-sm text-stone-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent" min="0" step="any" data-id="element-1343" />
                                                                    </div>
                                                                  </div> : <div className="grid grid-cols-3 gap-3" data-id="element-1344">
                                                                    <div data-id="element-1345">
                                                                      <label className="block text-xs font-medium text-stone-600 dark:text-stone-300 mb-1" data-id="element-1346">
                                                                        Base (
                                                                        {calcUnit}
                                                                        )
                                                                      </label>
                                                                      <input type="number" value={inlineCalcBase} onChange={e => setInlineCalcBase(e.target.value)} placeholder="0" className="w-full px-3 py-2 bg-white dark:bg-[#252525] border border-stone-300 dark:border-[#404040] rounded-lg text-sm text-stone-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent" min="0" step="any" data-id="element-1347" />
                                                                    </div>
                                                                    <div data-id="element-1348">
                                                                      <label className="block text-xs font-medium text-stone-600 dark:text-stone-300 mb-1" data-id="element-1349">
                                                                        Height (
                                                                        {calcUnit}
                                                                        )
                                                                      </label>
                                                                      <input type="number" value={inlineCalcHeight} onChange={e => setInlineCalcHeight(e.target.value)} placeholder="0" className="w-full px-3 py-2 bg-white dark:bg-[#252525] border border-stone-300 dark:border-[#404040] rounded-lg text-sm text-stone-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent" min="0" step="any" data-id="element-1350" />
                                                                    </div>
                                                                    <div data-id="element-1351">
                                                                      <label className="block text-xs font-medium text-stone-600 dark:text-stone-300 mb-1" data-id="element-1352">
                                                                        Depth (
                                                                        {calcDepthUnit}
                                                                        )
                                                                      </label>
                                                                      <input type="number" value={inlineCalcDepth} onChange={e => setInlineCalcDepth(e.target.value)} placeholder="3" className="w-full px-3 py-2 bg-white dark:bg-[#252525] border border-stone-300 dark:border-[#404040] rounded-lg text-sm text-stone-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent" min="0" step="any" data-id="element-1353" />
                                                                    </div>
                                                                  </div>}

                                                                {(() => {
                                                // Convert all dimensions to feet
                                                const mToFt = 3.28084;
                                                const toFt = (v: number) => calcUnit === 'm' ? v * mToFt : v;
                                                const l = toFt(parseFloat(inlineCalcLength) || 0);
                                                const w = toFt(parseFloat(inlineCalcWidth) || 0);
                                                const dia = toFt(parseFloat(inlineCalcDiameter) || 0);
                                                const b = toFt(parseFloat(inlineCalcBase) || 0);
                                                const h = toFt(parseFloat(inlineCalcHeight) || 0);
                                                const dpRaw = parseFloat(inlineCalcDepth) || 0;
                                                const dpFt = calcDepthUnit === 'cm' ? dpRaw / 30.48 : dpRaw / 12;
                                                let areaSqFt = 0;
                                                if (calcShape === 'rectangle' || calcShape === 'irregular') {
                                                  areaSqFt = l * w;
                                                } else if (calcShape === 'circle') {
                                                  const r = dia / 2;
                                                  areaSqFt = Math.PI * r * r;
                                                } else {
                                                  areaSqFt = 0.5 * b * h;
                                                }
                                                const cubicYards = areaSqFt * dpFt / 27;
                                                const density = getDensity(product.name);
                                                const tons = cubicYards * density;
                                                const roundedYards = Math.max(1, Math.ceil(cubicYards));
                                                const hasInput = areaSqFt > 0 && dpFt > 0;
                                                if (!hasInput) return null;
                                                return <div className="pt-3 border-t border-stone-200 dark:border-[#404040] space-y-3" data-id="element-1354">
                                                                      <div className="grid grid-cols-2 gap-3" data-id="element-1355">
                                                                        <div className="bg-white dark:bg-[#252525] rounded-lg p-3 border border-stone-200 dark:border-[#404040]" data-id="element-1356">
                                                                          <div className="text-[10px] font-bold uppercase tracking-wider text-stone-500 dark:text-stone-400 mb-0.5" data-id="element-1357">
                                                                            Cubic
                                                                            Yards
                                                                          </div>
                                                                          <div className="text-2xl font-bold text-stone-900 dark:text-white leading-none" data-id="element-1358">
                                                                            {cubicYards.toFixed(1)}
                                                                          </div>
                                                                        </div>
                                                                        <div className="bg-white dark:bg-[#252525] rounded-lg p-3 border border-stone-200 dark:border-[#404040]" data-id="element-1359">
                                                                          <div className="text-[10px] font-bold uppercase tracking-wider text-stone-500 dark:text-stone-400 mb-0.5" data-id="element-1360">
                                                                            Est.
                                                                            Tons
                                                                          </div>
                                                                          <div className="text-2xl font-bold text-stone-900 dark:text-white leading-none" data-id="element-1361">
                                                                            {tons.toFixed(1)}
                                                                          </div>
                                                                        </div>
                                                                      </div>
                                                                      <button onClick={() => {
                                                    const isLight = isLightMaterial(product.name);
                                                    const maxQty = isLight ? 60 : 48;
                                                    const minQty = 3;
                                                    const snapped = Math.min(maxQty, Math.max(minQty, roundedYards));
                                                    setSelectedQuantity(snapped);
                                                    setShowInlineCalc(false);
                                                  }} className="w-full py-3 bg-brand-button hover:bg-brand-buttonHover text-white text-sm font-bold rounded-lg transition-colors flex items-center justify-center gap-2 shadow-[0_0_12px_rgba(5,137,77,0.4)]" data-id="element-1362">
                                                                        <Check className="w-4 h-4" data-id="element-1363" />
                                                                        Use{' '}
                                                                        {roundedYards}{' '}
                                                                        Yards
                                                                      </button>
                                                                      <p className="text-[10px] text-stone-500 dark:text-stone-400 text-center leading-relaxed" data-id="element-1364">
                                                                        Tons
                                                                        estimated
                                                                        using
                                                                        density
                                                                        for{' '}
                                                                        <span className="font-semibold text-stone-700 dark:text-stone-300" data-id="element-1365">
                                                                          {product.name}
                                                                        </span>
                                                                        . Add
                                                                        ~10% for
                                                                        compaction
                                                                        & waste.
                                                                      </p>
                                                                    </div>;
                                              })()}
                                                              </div>
                                                            </motion.div>}
                                                        </AnimatePresence>
                                                      </div>

                                                      <div className="mt-4 pt-3 border-t border-stone-200 dark:border-[#3a3a3a] flex items-center justify-center gap-3 text-xs" data-id="element-1366">
                                                        <button onClick={() => {
                                          resetQuiz();
                                          setShowQuizModal(true);
                                        }} className="flex items-center gap-1.5 text-stone-600 dark:text-white/60 hover:text-brand-orange dark:hover:text-brand-orange transition-colors font-medium" data-id="element-1367">
                                                          <Info className="w-3.5 h-3.5" data-id="element-1368" />
                                                          <span data-id="element-1369">
                                                            Not sure? Take the
                                                            quiz
                                                          </span>
                                                        </button>
                                                        <span className="text-stone-300 dark:text-white/20" aria-hidden="true" data-id="element-1370">
                                                          ·
                                                        </span>
                                                        <Link to={`/compare?add=${encodeURIComponent(product.slug)}`} className="flex items-center gap-1.5 text-stone-600 dark:text-white/60 hover:text-brand-orange dark:hover:text-brand-orange transition-colors font-medium" data-id="element-1371">
                                                          <RefreshCw className="w-3.5 h-3.5" data-id="element-1372" />
                                                          <span data-id="element-1373">
                                                            Compare materials
                                                          </span>
                                                        </Link>
                                                      </div>
                                                    </>}
                                                </div>
                                              </> : <div className="mt-auto space-y-4" data-id="element-1374">
                                                <a href="tel:206-619-3742" className="w-full bg-brand-charcoal dark:bg-white text-white dark:text-brand-charcoal hover:bg-brand-charcoal/90 dark:hover:bg-stone-200 py-3.5 rounded-xl font-bold transition-colors flex items-center justify-center" data-id="element-1375">
                                                  <Phone className="w-5 h-5 mr-2" data-id="element-1376" />
                                                  Call for Pricing
                                                </a>
                                                <button onClick={() => setIsQuoteModalOpen(true)} className="w-full bg-brand-button hover:bg-brand-buttonHover text-white py-3.5 rounded-xl font-bold transition-colors" data-id="element-1377">
                                                  Get a Custom Quote
                                                </button>
                                              </div>}
                                          </div>
                                        </div>}
                                    </>}
                                </div>
                              </motion.div>}
                          </AnimatePresence>
                        </div>;
                }
                return <div key={category.name} id={`cat-${category.name}`} className="overflow-hidden scroll-mt-[135px]" data-id="element-1378">
                        <button onClick={() => {
                    setExpandedCategory(category.name);
                    setCategoryOverview(category.name);
                    setCarouselIndex(0);
                    setTimeout(() => {
                      document.getElementById(`cat-${category.name}`)?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start'
                      });
                    }, 100);
                  }} className="w-full flex items-center justify-between py-3 sm:py-4 text-left focus:outline-none group" data-id="element-1379">
                          <div data-id="element-1380">
                            <h3 className={`font-heading text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight transition-colors duration-200 text-brand-charcoal dark:text-white/80 group-hover:text-brand-orange`} data-id="element-1381">
                              {category.name}
                            </h3>
                            <span className="text-[11px] font-normal text-stone-400 dark:text-white/40 tracking-wide" data-id="element-1382">
                              {category.name === 'Soil, Mulch & Org.' ? '15' : '12'}{' '}
                              yards max load
                            </span>
                          </div>
                          <ChevronDown className="w-7 h-7 shrink-0 ml-4 transition-transform duration-300 text-brand-charcoal/30 dark:text-white/30 group-hover:text-brand-orange" data-id="element-1383" />
                        </button>
                      </div>;
              })}

                  {/* View Full Catalog Button & Expanded Grid */}
                  <div className="pt-6" data-id="element-1384">
                    <button onClick={() => {
                  const wasOpen = showAllCatalog;
                  setShowAllCatalog(!showAllCatalog);
                  if (wasOpen) {
                    setTimeout(() => {
                      document.getElementById('materials')?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start'
                      });
                    }, 450);
                  }
                }} className="mx-auto flex items-center gap-2 py-2 px-4 text-stone-800 dark:text-stone-300 hover:text-brand-orange transition-colors text-xs uppercase tracking-wider" data-id="element-1385">
                      <motion.span animate={{
                    rotate: showAllCatalog ? 45 : 0
                  }} transition={{
                    duration: 0.2
                  }} className="w-5 h-5 flex items-center justify-center" data-id="element-1386">
                        <span className="text-sm font-bold leading-none" data-id="element-1387">
                          +
                        </span>
                      </motion.span>
                      <span className="font-bold text-sm uppercase tracking-wider" data-id="element-1388">
                        {showAllCatalog ? 'Collapse Catalog' : 'View Full Catalog'}
                      </span>
                    </button>

                    <AnimatePresence data-id="element-1389">
                      {showAllCatalog && <motion.div initial={{
                    opacity: 0,
                    height: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto'
                  }} exit={{
                    opacity: 0,
                    height: 0
                  }} transition={{
                    duration: 0.4,
                    ease: 'easeInOut'
                  }} className="overflow-hidden" data-id="element-1390">
                          <div className="pt-8 space-y-12" data-id="element-1391">
                            {categories.map(category => <div key={`catalog-${category.name}`} data-id="element-1392">
                                <h3 className="font-heading text-2xl font-bold text-brand-charcoal dark:text-white mb-5 flex items-center" data-id="element-1393">
                                  <span className="w-2 h-7 bg-brand-orange mr-3 rounded-sm" data-id="element-1394" />
                                  {category.name}
                                </h3>
                                <div className="space-y-1.5" data-id="element-1395">
                                  {category.products.map(product => <button key={product.slug} onClick={() => {
                            setShowAllCatalog(false);
                            setExpandedCategory(category.name);
                            setCategoryOverview(null);
                            setCarouselIndex(category.products.findIndex(p => p.slug === product.slug));
                            setTimeout(() => {
                              document.getElementById(`cat-${category.name}`)?.scrollIntoView({
                                behavior: 'smooth',
                                block: 'start'
                              });
                            }, 100);
                          }} className="w-full relative overflow-hidden rounded-xl group/card text-left block" style={{
                            height: '200px'
                          }} data-id="element-1396">
                                      <img src={product.images?.[0] || product.image} alt={product.name} className="absolute inset-0 w-full h-full object-cover group-hover/card:scale-105 transition-transform duration-500" data-id="element-1397" />
                                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" data-id="element-1398" />
                                      <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6" data-id="element-1399">
                                        <h4 className="font-heading text-2xl sm:text-3xl font-bold text-white leading-tight mb-1" data-id="element-1400">
                                          {product.name}
                                        </h4>
                                        <p className="text-white/90 text-sm sm:text-base font-medium" data-id="element-1401">
                                          ${product.pricePerYard} / yard
                                        </p>
                                      </div>
                                    </button>)}
                                </div>
                              </div>)}
                          </div>
                          <button onClick={() => {
                      setShowAllCatalog(false);
                      setTimeout(() => {
                        document.getElementById('materials')?.scrollIntoView({
                          behavior: 'smooth',
                          block: 'start'
                        });
                      }, 450);
                    }} className="mx-auto flex items-center gap-2 mt-10 py-3 px-6 text-stone-500 dark:text-stone-400 hover:text-brand-orange transition-colors text-sm font-bold uppercase tracking-wider border border-stone-200 dark:border-[#404040] hover:border-brand-orange/50 rounded-lg" data-id="element-1402">
                            <ChevronDown className="w-4 h-4 rotate-180" data-id="element-1403" />
                            Collapse Catalog
                          </button>
                        </motion.div>}
                    </AnimatePresence>
                  </div>
                </motion.div>}
              {/* CALCULATOR TAB */}
              {activeTab === 'calculator' && <motion.div key="calculator" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.2
            }} className="max-w-4xl mx-auto" data-id="element-1404">
                  <div className="bg-[#fdfbf7] dark:bg-[#232221] rounded-2xl border border-stone-200 dark:border-[#333231] shadow-sm overflow-hidden transition-colors duration-300" data-id="element-1405">
                    <div className="grid grid-cols-1 md:grid-cols-5" data-id="element-1406">
                      <div className="p-6 sm:p-8 md:col-span-3 border-b md:border-b-0 md:border-r border-stone-200 dark:border-[#333231]" data-id="element-1407">
                        <form onSubmit={handleCalculate} className="space-y-6" data-id="element-1408">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1409">
                            <div data-id="element-1410">
                              <label className="block text-sm font-medium text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-1411">
                                Project Type
                              </label>
                              <CustomSelect value={projectType} onChange={e => setProjectType(e)} options={projectTypes.map(p => ({
                            value: p.id,
                            label: p.name
                          }))} placeholder="Select a project type" data-id="element-1412" />
                            </div>
                            <div data-id="element-1413">
                              <label className="block text-sm font-medium text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-1414">
                                Material
                              </label>
                              <CustomSelect value={material} onChange={e => setMaterial(e)} options={calcMaterials.map(m => ({
                            value: m.id,
                            label: m.name
                          }))} placeholder="Select a material" data-id="element-1415" />
                            </div>
                          </div>

                          <div className="grid grid-cols-3 gap-4" data-id="element-1416">
                            <div data-id="element-1417">
                              <label className="block text-sm font-medium text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-1418">
                                Length (ft)
                              </label>
                              <input type="number" value={length} onChange={e => setLength(e.target.value)} placeholder="0" className="w-full px-4 py-3 bg-[#f5f2ee] dark:bg-[#1a1918] border border-stone-300 dark:border-[#2a2928] rounded-xl text-brand-charcoal dark:text-white placeholder-brand-muted dark:placeholder-stone-600 focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all" required min="0.1" step="any" data-id="element-1419" />
                            </div>
                            <div data-id="element-1420">
                              <label className="block text-sm font-medium text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-1421">
                                Width (ft)
                              </label>
                              <input type="number" value={width} onChange={e => setWidth(e.target.value)} placeholder="0" className="w-full px-4 py-3 bg-[#f5f2ee] dark:bg-[#1a1918] border border-stone-300 dark:border-[#2a2928] rounded-xl text-brand-charcoal dark:text-white placeholder-brand-muted dark:placeholder-stone-600 focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all" required min="0.1" step="any" data-id="element-1422" />
                            </div>
                            <div data-id="element-1423">
                              <label className="block text-sm font-medium text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-1424">
                                Depth (in)
                              </label>
                              <input type="number" value={depth} onChange={e => setDepth(e.target.value)} className="w-full px-4 py-3 bg-[#f5f2ee] dark:bg-[#1a1918] border border-stone-300 dark:border-[#2a2928] rounded-xl text-brand-charcoal dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all" required min="0.1" step="any" data-id="element-1425" />
                            </div>
                          </div>

                          <button type="submit" className="w-full bg-brand-button hover:bg-brand-buttonHover text-white py-4 rounded-xl font-medium transition-colors flex items-center justify-center" data-id="element-1426">
                            <Calculator className="w-5 h-5 mr-2" data-id="element-1427" />
                            Calculate Need
                          </button>
                        </form>
                      </div>

                      <div className="p-6 sm:p-8 md:col-span-2 bg-[#f3f0ec] dark:bg-[#1e1d1c] flex flex-col justify-center transition-colors duration-300" data-id="element-1428">
                        {calcResults ? <div className="space-y-6" data-id="element-1429">
                            <div data-id="element-1430">
                              <p className="text-sm text-brand-muted dark:text-stone-400 font-medium mb-1" data-id="element-1431">
                                Estimated Volume
                              </p>
                              <div className="flex items-baseline gap-2" data-id="element-1432">
                                <span className="font-heading text-4xl font-bold text-brand-charcoal dark:text-white" data-id="element-1433">
                                  {calcResults.yards}
                                </span>
                                <span className="text-brand-muted dark:text-stone-400 font-medium" data-id="element-1434">
                                  Cubic Yards
                                </span>
                              </div>
                            </div>
                            <div className="pt-4 border-t border-stone-200 dark:border-[#262626]" data-id="element-1435">
                              <p className="text-sm text-brand-muted dark:text-stone-400 font-medium mb-1" data-id="element-1436">
                                Estimated Weight
                              </p>
                              <div className="flex items-baseline gap-2" data-id="element-1437">
                                <span className="font-heading text-3xl font-bold text-brand-charcoal dark:text-white" data-id="element-1438">
                                  {calcResults.tons}
                                </span>
                                <span className="text-brand-muted dark:text-stone-400 font-medium" data-id="element-1439">
                                  Tons
                                </span>
                              </div>
                            </div>
                            {(() => {
                        const mat = calcMaterials.find(m => m.id === material);
                        const basePrice = mat ? parseInt(mat.priceRange.replace('$', '')) : 0;
                        const yards = calcResults?.yards || 0;
                        const roundedYards = Math.ceil(yards);
                        const minCharge = 3;
                        const smallThreshold = 10;
                        const fullLoad = 12;
                        const billable = Math.max(roundedYards, minCharge);
                        const isBeyondFull = roundedYards > fullLoad;
                        const bulkDiscount = roundedYards >= 24;
                        const calcLoad = (y: number) => {
                          const b = Math.max(y, minCharge);
                          let s = 1;
                          if (b < smallThreshold) {
                            if (b >= 5) {
                              s = 1 + (smallThreshold - b) * 0.1;
                            } else {
                              const at5 = 1 + (smallThreshold - 5) * 0.1;
                              s = at5 + (5 - b) * 0.15;
                            }
                          }
                          return b * Math.ceil(basePrice * s);
                        };
                        let total = 0;
                        if (isBeyondFull) {
                          total = calcLoad(fullLoad) + calcLoad(roundedYards - fullLoad);
                        } else {
                          total = calcLoad(roundedYards);
                        }
                        const preDiscount = total;
                        if (bulkDiscount) total = Math.round(total * 0.95);
                        const surcharge = billable < smallThreshold ? 1 + (smallThreshold - billable) * 0.1 : 1;
                        const effectivePrice = Math.ceil(basePrice * surcharge);
                        return <div className="pt-4 border-t border-stone-200 dark:border-[#4b4b4b]" data-id="element-1440">
                                  <p className="text-sm text-stone-500 dark:text-stone-400 font-medium mb-1" data-id="element-1441">
                                    Estimated Cost
                                  </p>
                                  <div className="flex items-baseline gap-2" data-id="element-1442">
                                    <span className="font-heading text-3xl font-bold text-stone-900 dark:text-white" data-id="element-1443">
                                      ${total}
                                    </span>
                                    <span className="text-stone-500 dark:text-stone-400 text-sm font-medium" data-id="element-1444">
                                      + delivery
                                    </span>
                                  </div>
                                  <p className="text-sm text-stone-600 dark:text-white/70 mt-1" data-id="element-1445">
                                    {roundedYards} yds × $
                                    {isBeyondFull ? basePrice : effectivePrice}
                                    /yd
                                  </p>
                                  {roundedYards < minCharge && roundedYards > 0 && <p className="text-xs text-brand-orange mt-1" data-id="element-1446">
                                        {minCharge} yard minimum charge applies
                                      </p>}
                                  {roundedYards >= minCharge && roundedYards < smallThreshold && <p className="text-xs text-brand-orange mt-1" data-id="element-1447">
                                        Small load upcharge · ${basePrice}/yd at{' '}
                                        {smallThreshold}+ yards
                                      </p>}
                                  {isBeyondFull && <p className="text-xs text-brand-orange mt-1" data-id="element-1448">
                                      2 deliveries required ({fullLoad} +{' '}
                                      {roundedYards - fullLoad} yds)
                                    </p>}
                                  {bulkDiscount && <p className="text-xs text-amber-600 dark:text-amber-400 mt-1 font-semibold" data-id="element-1449">
                                      5% bulk discount applied · was $
                                      {preDiscount}
                                    </p>}
                                  <p className="text-xs text-stone-500 mt-2 flex items-start" data-id="element-1450">
                                    <Info className="w-3 h-3 mr-1 mt-0.5 shrink-0" data-id="element-1451" />
                                    Delivery fee not included. Final price
                                    depends on exact location.
                                  </p>
                                </div>;
                      })()}
                          </div> : <div className="text-center text-brand-muted dark:text-stone-500" data-id="element-1452">
                            <Calculator className="w-12 h-12 mx-auto mb-4 opacity-50" data-id="element-1453" />
                            <p data-id="element-1454">
                              Enter your project dimensions to see estimated
                              materials needed.
                            </p>
                          </div>}
                      </div>
                    </div>
                  </div>
                </motion.div>}
              {/* QUIZ TAB */}
              {activeTab === 'quiz' && <motion.div key="quiz" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.2
            }} className="max-w-2xl mx-auto" data-id="element-1455">
                  <div className="bg-[#fdfbf7] dark:bg-[#232221] rounded-2xl border border-stone-200 dark:border-[#333231] shadow-sm p-8 text-center transition-colors duration-300" data-id="element-1456">
                    {quizResult ? <motion.div initial={{
                  opacity: 0,
                  scale: 0.95
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} data-id="element-1457">
                        <div className="w-16 h-16 bg-brand-orange/10 dark:bg-brand-orange/20 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1458">
                          <CheckCircle2 className="w-8 h-8 text-brand-orange" data-id="element-1459" />
                        </div>
                        <h3 className="text-xl text-brand-muted dark:text-stone-200 mb-2" data-id="element-1460">
                          We recommend:
                        </h3>
                        <h2 className="font-heading text-3xl font-bold text-brand-charcoal dark:text-white mb-4" data-id="element-1461">
                          {quizResult.name}
                        </h2>
                        <p className="text-brand-muted dark:text-stone-200 mb-8 max-w-md mx-auto" data-id="element-1462">
                          {quizResult.desc}
                        </p>

                        <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1463">
                          <button onClick={() => setIsQuoteModalOpen(true)} className="bg-brand-button hover:bg-brand-buttonHover text-white px-8 py-3 rounded-xl font-bold transition-colors" data-id="element-1464">
                            Get a Quote
                          </button>
                          <button onClick={resetQuiz} className="flex items-center justify-center px-8 py-3 rounded-xl font-bold text-brand-charcoal dark:text-white bg-brand-lightgray dark:bg-[#3a3a3a] hover:bg-stone-200 dark:hover:bg-[#4b4b4b] transition-colors" data-id="element-1465">
                            <RefreshCw className="w-4 h-4 mr-2" data-id="element-1466" />
                            Start Over
                          </button>
                        </div>
                      </motion.div> : currentQuizQuestion ? <motion.div key={currentQuizQuestion.id} initial={{
                  opacity: 0,
                  x: 20
                }} animate={{
                  opacity: 1,
                  x: 0
                }} data-id="element-1467">
                        <h3 className="font-heading text-2xl font-bold text-brand-charcoal dark:text-white mb-8" data-id="element-1468">
                          {currentQuizQuestion.question}
                        </h3>
                        <div className="space-y-3" data-id="element-1469">
                          {currentQuizQuestion.options.map(option => <button key={option.value} onClick={() => handleQuizAnswer(currentQuizQuestion.id, option.value, option.result)} className="w-full text-left p-4 rounded-xl border border-stone-200 dark:border-[#333231] bg-white dark:bg-[#1a1918] hover:border-brand-orange dark:hover:border-brand-orange hover:bg-brand-orange/5 dark:hover:bg-brand-orange/10 transition-all group" data-id="element-1470">
                              <div className="flex items-center justify-between" data-id="element-1471">
                                <span className="font-medium text-stone-900 dark:text-white group-hover:text-brand-orange transition-colors" data-id="element-1472">
                                  {option.label}
                                </span>
                                <ArrowRight className="w-5 h-5 text-stone-500 dark:text-stone-400 group-hover:text-brand-orange group-hover:translate-x-1 transition-all" data-id="element-1473" />
                              </div>
                            </button>)}
                        </div>
                      </motion.div> : null}
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>
        </div>

        {/* Can't find what you need? */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.2
      }} className="mt-16 flex flex-col md:flex-row items-center justify-between gap-6 bg-transparent border border-stone-200/60 dark:border-[#404040] rounded-2xl px-8 py-6 max-w-7xl mx-auto" data-id="element-1474">
          <div className="text-center md:text-left" data-id="element-1475">
            <p className="text-stone-700 dark:text-stone-300 text-lg font-sub mb-1" data-id="element-1476">
              Can't find what you're looking for?
            </p>
            <p className="text-stone-500 dark:text-stone-400 text-sm" data-id="element-1477">
              We source a wide range of materials beyond what's listed here.
              Tell us what you need and we'll find it for you.
            </p>
          </div>
          <Link to="/contact" className="inline-flex items-center gap-2 bg-brand-button hover:bg-brand-buttonHover text-white px-8 py-3 rounded-full font-bold transition-colors shrink-0" data-id="element-1478">
            Contact Us
            <ArrowRight className="w-4 h-4" data-id="element-1479" />
          </Link>
        </motion.div>
      </section>

      {/* Calculator Modal */}
      <AnimatePresence data-id="element-1480">
        {showCalcModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-1481">
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setShowCalcModal(false)} className="absolute inset-0 bg-black/60 backdrop-blur-sm" data-id="element-1482" />
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} className="relative w-full max-w-4xl bg-white dark:bg-[#3d3d3d] border border-stone-200 dark:border-[#4b4b4b] rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh]" data-id="element-1483">
              <div className="flex items-center justify-between p-6 border-b border-stone-200 dark:border-[#4b4b4b] shrink-0" data-id="element-1484">
                <h3 className="text-xl font-bold text-stone-900 dark:text-white flex items-center" data-id="element-1485">
                  <Calculator className="w-5 h-5 mr-2 text-brand-orange" data-id="element-1486" />
                  Material Calculator
                </h3>
                <button onClick={() => setShowCalcModal(false)} className="text-stone-400 hover:text-stone-900 dark:hover:text-white transition-colors p-1" data-id="element-1487">
                  <X className="w-6 h-6" data-id="element-1488" />
                </button>
              </div>
              <div className="overflow-y-auto" data-id="element-1489">
                <div className="grid grid-cols-1 md:grid-cols-5" data-id="element-1490">
                  <div className="p-6 sm:p-8 md:col-span-3 border-b md:border-b-0 md:border-r border-stone-200 dark:border-[#4b4b4b]" data-id="element-1491">
                    <form onSubmit={handleCalculate} className="space-y-6" data-id="element-1492">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1493">
                        <div data-id="element-1494">
                          <label className="block text-sm font-medium text-stone-600 dark:text-stone-300 mb-2" data-id="element-1495">
                            Project Type
                          </label>
                          <CustomSelect value={projectType} onChange={e => setProjectType(e)} options={projectTypes.map(p => ({
                        value: p.id,
                        label: p.name
                      }))} placeholder="Select a project type" data-id="element-1496" />
                        </div>
                        <div data-id="element-1497">
                          <label className="block text-sm font-medium text-stone-600 dark:text-stone-300 mb-2" data-id="element-1498">
                            Material
                          </label>
                          <CustomSelect value={material} onChange={e => setMaterial(e)} options={calcMaterials.map(m => ({
                        value: m.id,
                        label: m.name
                      }))} placeholder="Select a material" data-id="element-1499" />
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-4" data-id="element-1500">
                        <div data-id="element-1501">
                          <label className="block text-sm font-medium text-stone-600 dark:text-stone-300 mb-2" data-id="element-1502">
                            Length (ft)
                          </label>
                          <input type="number" value={length} onChange={e => setLength(e.target.value)} placeholder="0" className="w-full px-4 py-3 bg-stone-100 dark:bg-[#303030] border border-stone-300 dark:border-[#4b4b4b] rounded-xl text-stone-900 dark:text-white placeholder-stone-400 focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all" required min="0.1" step="any" data-id="element-1503" />
                        </div>
                        <div data-id="element-1504">
                          <label className="block text-sm font-medium text-stone-600 dark:text-stone-300 mb-2" data-id="element-1505">
                            Width (ft)
                          </label>
                          <input type="number" value={width} onChange={e => setWidth(e.target.value)} placeholder="0" className="w-full px-4 py-3 bg-stone-100 dark:bg-[#303030] border border-stone-300 dark:border-[#4b4b4b] rounded-xl text-stone-900 dark:text-white placeholder-stone-400 focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all" required min="0.1" step="any" data-id="element-1506" />
                        </div>
                        <div data-id="element-1507">
                          <label className="block text-sm font-medium text-stone-600 dark:text-stone-300 mb-2" data-id="element-1508">
                            Depth (in)
                          </label>
                          <input type="number" value={depth} onChange={e => setDepth(e.target.value)} className="w-full px-4 py-3 bg-stone-100 dark:bg-[#303030] border border-stone-300 dark:border-[#4b4b4b] rounded-xl text-stone-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all" required min="0.1" step="any" data-id="element-1509" />
                        </div>
                      </div>

                      <button type="submit" className="w-full bg-brand-button hover:bg-brand-buttonHover text-white py-4 rounded-xl font-medium transition-colors flex items-center justify-center" data-id="element-1510">
                        <Calculator className="w-5 h-5 mr-2" data-id="element-1511" />
                        Calculate Need
                      </button>
                    </form>
                  </div>

                  <div className="p-6 sm:p-8 md:col-span-2 bg-stone-50 dark:bg-[#303030] flex flex-col justify-center" data-id="element-1512">
                    {calcResults ? <div className="space-y-6" data-id="element-1513">
                        <div data-id="element-1514">
                          <p className="text-sm text-stone-500 dark:text-stone-400 font-medium mb-1" data-id="element-1515">
                            Estimated Volume
                          </p>
                          <div className="flex items-baseline gap-2" data-id="element-1516">
                            <span className="font-heading text-4xl font-bold text-stone-900 dark:text-white" data-id="element-1517">
                              {calcResults.yards}
                            </span>
                            <span className="text-stone-500 dark:text-stone-400 font-medium" data-id="element-1518">
                              Cubic Yards
                            </span>
                          </div>
                        </div>
                        <div className="pt-4 border-t border-stone-200 dark:border-[#4b4b4b]" data-id="element-1519">
                          <p className="text-sm text-stone-500 dark:text-stone-400 font-medium mb-1" data-id="element-1520">
                            Estimated Weight
                          </p>
                          <div className="flex items-baseline gap-2" data-id="element-1521">
                            <span className="font-heading text-3xl font-bold text-stone-900 dark:text-white" data-id="element-1522">
                              {calcResults.tons}
                            </span>
                            <span className="text-stone-500 dark:text-stone-400 font-medium" data-id="element-1523">
                              Tons
                            </span>
                          </div>
                        </div>
                        <div className="pt-4 border-t border-stone-200 dark:border-[#4b4b4b]" data-id="element-1524">
                          <p className="text-sm text-stone-500 dark:text-stone-400 font-medium mb-1" data-id="element-1525">
                            Material Price Range
                          </p>
                          <p className="text-lg font-medium text-stone-900 dark:text-white" data-id="element-1526">
                            {calcMaterials.find(m => m.id === material)?.priceRange}{' '}
                            <span className="text-sm text-stone-500 font-normal" data-id="element-1527">
                              / yard
                            </span>
                          </p>
                          <p className="text-xs text-stone-500 mt-1 flex items-start" data-id="element-1528">
                            <Info className="w-3 h-3 mr-1 mt-0.5 shrink-0" data-id="element-1529" />
                            Delivery fee not included. Final price depends on
                            exact location.
                          </p>
                        </div>
                      </div> : <div className="text-center text-stone-400 dark:text-stone-500" data-id="element-1530">
                        <Calculator className="w-12 h-12 mx-auto mb-4 opacity-50" data-id="element-1531" />
                        <p data-id="element-1532">
                          Enter your project dimensions to see estimated
                          materials needed.
                        </p>
                      </div>}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Quiz Modal */}
      <AnimatePresence data-id="element-1533">
        {showQuizModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-1534">
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setShowQuizModal(false)} className="absolute inset-0 bg-black/60 backdrop-blur-sm" data-id="element-1535" />
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} className="relative w-full max-w-2xl bg-white dark:bg-[#3d3d3d] border border-stone-200 dark:border-[#4b4b4b] rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh]" data-id="element-1536">
              <div className="flex items-center justify-between p-6 border-b border-stone-200 dark:border-[#4b4b4b] shrink-0" data-id="element-1537">
                <h3 className="text-xl font-bold text-stone-900 dark:text-white flex items-center" data-id="element-1538">
                  <ArrowRight className="w-5 h-5 mr-2 text-brand-orange" data-id="element-1539" />
                  Help Me Choose
                </h3>
                <button onClick={() => setShowQuizModal(false)} className="text-stone-400 hover:text-stone-900 dark:hover:text-white transition-colors p-1" data-id="element-1540">
                  <X className="w-6 h-6" data-id="element-1541" />
                </button>
              </div>
              <div className="overflow-y-auto p-8 text-center" data-id="element-1542">
                {quizResult ? <motion.div initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} data-id="element-1543">
                    <div className="w-16 h-16 bg-brand-orange/20 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1544">
                      <CheckCircle2 className="w-8 h-8 text-brand-orange" data-id="element-1545" />
                    </div>
                    <h3 className="text-xl text-stone-500 dark:text-stone-400 mb-2" data-id="element-1546">
                      We recommend:
                    </h3>
                    <h2 className="font-heading text-3xl font-bold text-stone-900 dark:text-white mb-4" data-id="element-1547">
                      {quizResult.name}
                    </h2>
                    <p className="text-stone-600 dark:text-stone-400 mb-8 max-w-md mx-auto" data-id="element-1548">
                      {quizResult.desc}
                    </p>

                    <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1549">
                      <button onClick={() => {
                  setShowQuizModal(false);
                  setIsQuoteModalOpen(true);
                }} className="bg-brand-button hover:bg-brand-buttonHover text-white px-8 py-3 rounded-xl font-bold transition-colors" data-id="element-1550">
                        Get a Quote
                      </button>
                      <button onClick={resetQuiz} className="flex items-center justify-center px-8 py-3 rounded-xl font-bold text-stone-900 dark:text-white bg-stone-100 dark:bg-[#3a3a3a] hover:bg-stone-200 dark:hover:bg-[#4b4b4b] transition-colors" data-id="element-1551">
                        <RefreshCw className="w-4 h-4 mr-2" data-id="element-1552" />
                        Start Over
                      </button>
                    </div>
                  </motion.div> : currentQuizQuestion ? <motion.div key={currentQuizQuestion.id} initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} data-id="element-1553">
                    <h3 className="font-heading text-2xl font-bold text-stone-900 dark:text-white mb-8" data-id="element-1554">
                      {currentQuizQuestion.question}
                    </h3>
                    <div className="space-y-3" data-id="element-1555">
                      {currentQuizQuestion.options.map(option => <button key={option.value} onClick={() => handleQuizAnswer(currentQuizQuestion.id, option.value, option.result)} className="w-full text-left p-4 rounded-xl border border-stone-200 dark:border-[#4b4b4b] bg-stone-50 dark:bg-[#303030] hover:border-brand-orange hover:bg-brand-orange/10 transition-all group" data-id="element-1556">
                          <div className="flex items-center justify-between" data-id="element-1557">
                            <span className="font-medium text-stone-900 dark:text-white group-hover:text-brand-orange transition-colors" data-id="element-1558">
                              {option.label}
                            </span>
                            <ArrowRight className="w-5 h-5 text-stone-300 dark:text-stone-700 group-hover:text-brand-orange group-hover:translate-x-1 transition-all" data-id="element-1559" />
                          </div>
                        </button>)}
                    </div>
                  </motion.div> : null}
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      <QuoteModal isOpen={isQuoteModalOpen} onClose={() => setIsQuoteModalOpen(false)} data-id="element-1560" />

      {/* Cart Added Toast removed — inline pill handles this now */}

      {/* Swipe Navigation Hint */}
      <AnimatePresence data-id="element-1561">
        {showSwipeHint && <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 30
      }} transition={{
        type: 'spring',
        stiffness: 300,
        damping: 25
      }} onClick={() => {
        setShowSwipeHint(false);
        setSwipeHintDismissed(true);
      }} className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] bg-brand-charcoal/95 dark:bg-[#2a2a2a]/95 text-white px-5 py-3 rounded-xl shadow-2xl border border-white/10 flex items-center gap-3 backdrop-blur-md cursor-pointer md:hidden" data-id="element-1562">
            <span className="text-2xl" data-id="element-1563">👆</span>
            <div data-id="element-1564">
              <p className="text-sm font-bold" data-id="element-1565">Swipe to browse products</p>
              <p className="text-xs text-white/60" data-id="element-1566">
                Swipe left or right to see the next material
              </p>
            </div>
            <X className="w-4 h-4 text-white/40 shrink-0 ml-2" data-id="element-1567" />
          </motion.div>}
      </AnimatePresence>
    </>;
}